import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCallout,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSmartTable,
  CSpinner,
} from "@coreui/react-pro";
import AdminPageFrame from "../../components/admin/AdminPageFrame";
import {
  acknowledgeAdminTrustFraudInbox,
  cleanupAdminTrustSimulation,
  getAdminTrustFraudCenter,
  getAdminTrustFraudEmployerDetail,
  getAdminTrustSimulationControl,
  runAdminTrustFraudAction,
  runAdminTrustSimulation,
  saveAdminTrustFraudRecipient,
  updateAdminTrustNotificationStatus,
} from "../../services/adminTrustFraudApi";
import {
  buildEmployerTrustHistoryRows,
  buildEmployerTrustIncidentRows,
  buildEmployerTrustReviewRows,
  buildEmployerTrustTaskRows,
  formatEmployerClassificationLabel,
  formatEmployerTrustFlagLabel,
  formatEmployerTypeLabel,
  formatPublishingRightsStageLabel,
  formatRiskLevelLabel,
  formatTrustIncidentStatusLabel,
  getEmployerTrustStatusColor,
  getPublishingRightsStageColor,
  getRiskLevelColor,
  getTrustIncidentStatusColor,
  getTrustReviewWorkflowStatusColor,
  getTrustTierColor,
  formatTrustReviewWorkflowStatusLabel,
  formatTrustTierLabel,
} from "../../services/employer/employerTrustDomain";

const QUEUE_OPTIONS = [
  { value: "all", label: "Alle Problemfaelle" },
  { value: "critical_cases", label: "Kritische Employer-Faelle" },
  { value: "open_reviews", label: "Offene Reviews" },
  { value: "incidents_complaints", label: "Aktive Incidents / Complaints" },
  { value: "publish_blocked", label: "Publish-blockierte Employer" },
  { value: "auto_paused_ads", label: "Auto-paused Ads" },
  { value: "escalated_cases", label: "Escalated Cases" },
];

const PRIORITY_OPTIONS = [
  { value: "all", label: "Alle Prioritaeten" },
  { value: "highest", label: "Highest priority" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const PRIORITY_META = {
  highest: { label: "Highest priority", color: "danger" },
  high: { label: "High", color: "danger" },
  medium: { label: "Medium", color: "warning" },
  low: { label: "Low", color: "secondary" },
};

const OPERATIONAL_PRIORITY_META = {
  requires_immediate_review: { label: "Requires Immediate Review", color: "danger", hint: "Akut, live oder schadensnah." },
  high_risk_but_contained: { label: "High Risk but Contained", color: "warning", hint: "Risikofall, aber noch eingehegt." },
  formal_review_pending: { label: "Formal Review Pending", color: "info", hint: "Formaler Blocker oder offener Review." },
  monitoring_low_priority: { label: "Monitoring / Low Priority", color: "secondary", hint: "Beobachten, aber nicht akut." },
};

const NOISE_CLASS_META = {
  formal_blocker: { label: "Formaler Blocker", color: "secondary" },
  operational_risk_case: { label: "Operativer Risikofall", color: "warning" },
  acute_intervention_case: { label: "Akuter Eingriffsfall", color: "danger" },
};

const IMPACT_META = {
  low: { label: "Low Impact", color: "secondary" },
  medium: { label: "Medium Impact", color: "info" },
  high: { label: "High Impact", color: "warning" },
  severe: { label: "Severe Impact", color: "danger" },
};

const ESCALATION_META = {
  none: { label: "Keine Eskalation", color: "secondary" },
  review: { label: "Review", color: "info" },
  urgent: { label: "Urgent", color: "warning" },
  critical: { label: "Critical", color: "danger" },
};

const HIGH_IMPACT_ACTIONS = new Set([
  "set_restricted",
  "set_publishing_hold",
  "complete_review_rejected",
  "pause_affected_ads",
  "release_affected_ads",
  "clear_under_investigation",
  "complete_review_verified",
]);

const INCIDENT_TYPE_OPTIONS = [
  { value: "fake_job", label: "Fake Job" },
  { value: "hidden_intermediary", label: "Hidden Intermediary" },
  { value: "wrong_employer_claim", label: "Wrong Employer Claim" },
  { value: "payment_requested", label: "Payment Requested" },
  { value: "false_promise_housing", label: "False Promise Housing" },
  { value: "interview_mismatch", label: "Interview Mismatch" },
  { value: "other", label: "Other" },
];

const INCIDENT_SEVERITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const NOTIFICATION_STATUS_META = {
  open: { label: "Open", color: "danger" },
  acknowledged: { label: "Acknowledged", color: "warning" },
  in_progress: { label: "In Progress", color: "info" },
  resolved: { label: "Resolved", color: "success" },
};

const SLA_META = {
  fresh: { label: "Fresh", color: "info" },
  aging: { label: "Aging", color: "warning" },
  overdue: { label: "Overdue", color: "danger" },
  neglected: { label: "Neglected", color: "danger" },
};

const OWNERSHIP_META = {
  unassigned: { label: "Unassigned", color: "danger" },
  assigned: { label: "Assigned", color: "info" },
  handed_over: { label: "Handed Over", color: "warning" },
  shared_watch: { label: "Shared Watch", color: "secondary" },
};

const ENFORCEMENT_META = {
  low_impact_enforcement: { label: "Low Impact Enforcement", color: "secondary" },
  standard_enforcement: { label: "Standard Enforcement", color: "info" },
  high_impact_enforcement: { label: "High Impact Enforcement", color: "warning" },
  severe_impact_enforcement: { label: "Severe Impact Enforcement", color: "danger" },
};

const RECIPIENT_SCOPE_OPTIONS = [
  { value: "trust_fraud", label: "Trust & Fraud" },
  { value: "all", label: "All" },
  { value: "security", label: "Security" },
  { value: "system", label: "System" },
];

const RECIPIENT_LEVEL_OPTIONS = [
  { value: "critical_only", label: "Critical only" },
  { value: "urgent_and_critical", label: "Urgent and critical" },
  { value: "all_operational", label: "All operational" },
  { value: "digest_only", label: "Digest only" },
];

const SIMULATION_MODE_OPTIONS = [
  { value: "structural", label: "Structural Simulation" },
  { value: "delivery", label: "Delivery Simulation" },
];

const OUTBOUND_STATUS_META = {
  pending: { label: "Pending", color: "warning" },
  sent: { label: "Sent", color: "success" },
  failed: { label: "Failed", color: "danger" },
  skipped: { label: "Skipped", color: "secondary" },
};

const ACTION_META = {
  start_review: {
    label: "Review starten",
    description: "Setzt die Trust-Pruefung aktiv in Gang und startet offene Review-Tasks.",
  },
  set_waiting_for_employer: {
    label: "Wartet auf Employer",
    description: "Markiert den Fall als wartend auf Rueckmeldung des Employers.",
    supportsNextAction: true,
  },
  set_waiting_for_evidence: {
    label: "Wartet auf Nachweise",
    description: "Markiert den Review als wartend auf weitere externe Evidenz.",
    supportsNextAction: true,
  },
  complete_review_verified: {
    label: "Review verifiziert abschliessen",
    description: "Schliesst den Review positiv ab und loest harte Trust-Blocker.",
  },
  complete_review_rejected: {
    label: "Review ablehnen",
    description: "Lehnt den Employer im Review ab und haelt Live-Publishing gesperrt.",
  },
  set_restricted: {
    label: "Employer restricted setzen",
    description: "Haertet den Trust-Fall operativ und blockiert Live-Publishing.",
  },
  set_publishing_hold: {
    label: "Publishing-Hold setzen",
    description: "Stoppt neue Live-Publikationen und signalisiert akuten Trust-Hold.",
  },
  release_publishing_hold: {
    label: "Publishing-Hold aufheben",
    description: "Hebt den Hold auf, sofern der Trust-Fall fachlich bereinigt ist.",
  },
  set_under_investigation: {
    label: "Unter Untersuchung setzen",
    description: "Markiert den Employer sichtbar als under investigation.",
  },
  clear_under_investigation: {
    label: "Untersuchung beenden",
    description: "Hebt den Untersuchungsstatus auf, wenn der Fall geklaert ist.",
  },
  trigger_recheck: {
    label: "Recheck ausloesen",
    description: "Startet eine erzwungene Re-Verifikation des Employers.",
  },
  confirm_incident: {
    label: "Incident bestaetigen",
    description: "Bestaetigt einen Incident und laesst ihn operativ wirken.",
    requiresIncident: true,
  },
  reject_incident: {
    label: "Incident zurueckweisen",
    description: "Verwirft einen Incident nach Pruefung.",
    requiresIncident: true,
  },
  resolve_incident: {
    label: "Incident als geloest markieren",
    description: "Schliesst einen bestaetigten oder offenen Incident als geloest.",
    requiresIncident: true,
  },
  complaint_to_incident: {
    label: "Complaint in Incident ueberfuehren",
    description: "Fuehrt ein Beschwerdesignal in einen echten Incident-Fall ueber.",
    supportsComplaintConversion: true,
  },
  pause_affected_ads: {
    label: "Betroffene Anzeigen pausieren",
    description: "Pausiert alle aktiven Anzeigen, die vom Trust-Fall betroffen sind.",
  },
  release_affected_ads: {
    label: "Betroffene Anzeigen wieder freigeben",
    description: "Reaktiviert nur Trust-seitig pausierte Anzeigen, wenn der Trust sauber ist.",
  },
  assign_to_me: {
    label: "Mir zuweisen",
    description: "Setzt dich als primaeren Owner fuer diesen operativen Fall.",
  },
  assign_to_admin: {
    label: "Admin zuweisen",
    description: "Weist den Fall einem konkreten Admin als primaeren Bearbeitungsanker zu.",
    requiresTargetAdmin: true,
  },
  handover_case: {
    label: "Fall uebergeben",
    description: "Uebergibt Ownership kontrolliert an einen anderen Admin.",
    requiresTargetAdmin: true,
  },
  release_owner: {
    label: "Owner loesen",
    description: "Loest die Ownership, wenn der Fall bewusst wieder unassigned gehen soll.",
  },
};

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("de-DE");
}

function formatPercent(value) {
  const numeric = Number(value || 0);
  return `${Math.round(numeric * 100)}%`;
}

function formatBoolean(value) {
  return value ? "Ja" : "Nein";
}

function formatAge(meta) {
  if (!meta) return "-";
  const hours = Number(meta.ageHours || 0);
  const days = Number(meta.ageDays || 0);
  if (!hours) return "0h";
  if (days >= 1) return `${days}d / ${hours}h`;
  return `${hours}h`;
}

function formatJson(value) {
  if (!value) return "-";
  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
}

function DetailField({ label, value }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3">
      <div className="text-uppercase text-body-secondary small">{label}</div>
      <div className="fw-semibold">{value || "-"}</div>
    </div>
  );
}

function QueueCard({ active, label, count, onClick, hint }) {
  return (
    <button
      type="button"
      className={`btn text-start border rounded p-3 h-100 ${active ? "btn-dark" : "btn-light"}`}
      onClick={onClick}
    >
      <div className="text-uppercase small opacity-75">Queue</div>
      <div className="fs-6 fw-semibold">{label}</div>
      <div className="mt-2 d-flex align-items-center justify-content-between">
        <strong className="fs-4">{count}</strong>
        <span className="small opacity-75">{hint}</span>
      </div>
    </button>
  );
}

function PriorityBadge({ level }) {
  const meta = PRIORITY_META[level] || PRIORITY_META.low;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function SeverityBadge({ severity }) {
  const color = severity === "critical" ? "danger" : severity === "high" ? "danger" : severity === "medium" ? "warning" : "secondary";
  return <CBadge color={color}>{String(severity || "low").toUpperCase()}</CBadge>;
}

function OperationalPriorityBadge({ value }) {
  const meta = OPERATIONAL_PRIORITY_META[value] || OPERATIONAL_PRIORITY_META.monitoring_low_priority;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function NoiseClassBadge({ value }) {
  const meta = NOISE_CLASS_META[value] || NOISE_CLASS_META.formal_blocker;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function ImpactBadge({ value }) {
  const meta = IMPACT_META[value] || IMPACT_META.low;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function EscalationBadge({ value }) {
  const meta = ESCALATION_META[value] || ESCALATION_META.none;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function NotificationStatusBadge({ value }) {
  const meta = NOTIFICATION_STATUS_META[value] || NOTIFICATION_STATUS_META.open;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function SlaBadge({ value }) {
  const meta = SLA_META[value] || SLA_META.fresh;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function OwnershipBadge({ value }) {
  const meta = OWNERSHIP_META[value] || OWNERSHIP_META.unassigned;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function EnforcementBadge({ value }) {
  const meta = ENFORCEMENT_META[value] || ENFORCEMENT_META.standard_enforcement;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function OutboundStatusBadge({ value }) {
  const meta = OUTBOUND_STATUS_META[value] || OUTBOUND_STATUS_META.pending;
  return <CBadge color={meta.color}>{meta.label}</CBadge>;
}

function formatOutboundTypeLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical_case_alert") return "Critical Case Alert";
  if (normalized === "urgent_escalation_alert") return "Urgent Escalation Alert";
  if (normalized === "confirmed_incident_alert") return "Confirmed Incident Alert";
  if (normalized === "sla_breach_alert") return "SLA Breach Alert";
  if (normalized === "daily_digest") return "Daily Digest";
  return value || "-";
}

function PriorityLaneCard({ active, label, count, hint, onClick, color = "secondary" }) {
  return (
    <button
      type="button"
      className={`btn text-start border rounded p-3 h-100 ${active ? `btn-${color}` : "btn-light"}`}
      onClick={onClick}
    >
      <div className="text-uppercase small opacity-75">Operative Prioritaet</div>
      <div className="fs-6 fw-semibold">{label}</div>
      <div className="mt-2 d-flex align-items-center justify-content-between gap-3">
        <strong className="fs-4">{count}</strong>
        <span className="small opacity-75">{hint}</span>
      </div>
    </button>
  );
}

function buildActionSafetySummary(action, employer) {
  if (!action || !employer) return null;
  const liveAds = Number(employer.affectedLiveAdsCount || employer.liveAdsCount || 0);
  const applicants = Number(employer.affectedApplicationsCount || 0);
  const trustBefore = employer.trustStatusLabel || employer.trustStatus || "-";
  const holdBefore = employer.publishingHold ? "aktiv" : "nicht aktiv";

  const summaries = {
    set_restricted: {
      before: `Vorher: Trust ${trustBefore}, Hold ${holdBefore}.`,
      after: "Nachher: Employer bleibt bzw. wird restricted, Publishing wird hart eingehegt.",
    },
    set_publishing_hold: {
      before: `Vorher: ${liveAds} Live-Anzeigen betroffen, Hold ${holdBefore}.`,
      after: "Nachher: Publishing-Hold aktiv, neue Live-Reichweite bleibt gesperrt.",
    },
    complete_review_rejected: {
      before: `Vorher: Review-Status ${employer.reviewWorkflowStatus || "-"}.`,
      after: "Nachher: Review abgelehnt, First-Ad-Review faellt negativ aus, Hold bleibt bzw. wird aktiv.",
    },
    pause_affected_ads: {
      before: `Vorher: ${liveAds} Live-Anzeigen / ${applicants} Bewerber mit Reichweite.`,
      after: "Nachher: betroffene Anzeigen werden pausiert und verlieren aktive Reichweite.",
    },
    release_affected_ads: {
      before: `Vorher: ${Number(employer.pausedAdsCount || 0)} pausierte Anzeigen / ${applicants} Bewerber betroffen.`,
      after: "Nachher: Trust-seitig pausierte Anzeigen koennen wieder aktiv werden, sofern der Trust sauber ist.",
    },
    clear_under_investigation: {
      before: `Vorher: Investigation ${employer.underInvestigation ? "aktiv" : "nicht aktiv"}.`,
      after: "Nachher: Untersuchung wird aufgehoben und der Fall faellt aus der akuten Eskalation.",
    },
    complete_review_verified: {
      before: `Vorher: Review-Status ${employer.reviewWorkflowStatus || "-"}, Hold ${holdBefore}.`,
      after: "Nachher: Review wird verifiziert abgeschlossen, harte Trust-Blocker koennen fallen.",
    },
    assign_to_me: {
      before: `Vorher: Owner ${employer.currentOwnerAdmin || "nicht gesetzt"}, Ownership ${employer.ownershipState || "unassigned"}.`,
      after: "Nachher: du bist primaerer Owner des Falls und operativer Bearbeitungsanker.",
    },
    assign_to_admin: {
      before: `Vorher: Owner ${employer.currentOwnerAdmin || "nicht gesetzt"}, Ownership ${employer.ownershipState || "unassigned"}.`,
      after: "Nachher: ein anderer Admin wird primaerer Owner dieses Falls.",
    },
    handover_case: {
      before: `Vorher: Owner ${employer.currentOwnerAdmin || "nicht gesetzt"}.`,
      after: "Nachher: Ownership wird kontrolliert an einen anderen Admin uebergeben.",
    },
    release_owner: {
      before: `Vorher: Owner ${employer.currentOwnerAdmin || "nicht gesetzt"}.`,
      after: "Nachher: der Fall verliert den primaeren Owner und wird sichtbar unassigned.",
    },
  };

  return summaries[action] || null;
}

export default function AdminTrustFraudCenter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [queueFilter, setQueueFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [operationalPriorityFilter, setOperationalPriorityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const [notificationDetail, setNotificationDetail] = useState(null);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionDraft, setActionDraft] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [simulationControl, setSimulationControl] = useState(null);
  const [simulationDraft, setSimulationDraft] = useState({
    mode: "structural",
    scenario_set_key: "reference_full",
    scenario_keys: [],
    notes: "",
    overrides_json: "",
  });
  const [simulationBusy, setSimulationBusy] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupDraft, setCleanupDraft] = useState({
    batch_id: "",
    scenario_key: "",
    cleanup_all: false,
    dry_run: true,
    notes: "",
  });
  const [cleanupResult, setCleanupResult] = useState(null);
  const [recipientDraft, setRecipientDraft] = useState(null);
  const [recipientBusy, setRecipientBusy] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadCenter = async () => {
    try {
      setLoading(true);
      const nextCenter = await getAdminTrustFraudCenter({
        queue: queueFilter,
        priority: priorityFilter,
        q: deferredSearch || undefined,
      });
      setCenter(nextCenter);
      setError("");
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Admin Trust & Fraud Center konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const loadSimulationControl = async () => {
    try {
      const nextControl = await getAdminTrustSimulationControl();
      setSimulationControl(nextControl);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Simulationssteuerung konnte nicht geladen werden.");
    }
  };

  useEffect(() => {
    loadCenter();
  }, [queueFilter, priorityFilter, deferredSearch, refreshTick]);

  useEffect(() => {
    loadSimulationControl();
  }, [refreshTick]);

  const reviewRows = useMemo(() => buildEmployerTrustReviewRows(detail?.profile || {}), [detail]);
  const taskRows = useMemo(() => buildEmployerTrustTaskRows(detail?.profile || {}), [detail]);
  const incidentRows = useMemo(() => buildEmployerTrustIncidentRows(detail?.profile || {}), [detail]);
  const historyRows = useMemo(() => buildEmployerTrustHistoryRows(detail?.profile || {}), [detail]);
  const timelineRows = useMemo(() => detail?.timeline || [], [detail]);
  const notificationRows = useMemo(() => center?.notifications || [], [center]);
  const recipientRows = useMemo(() => center?.recipients || [], [center]);
  const outboundSummary = center?.outbound || null;
  const outboundDeliveryRows = useMemo(() => detail?.outbound_deliveries || [], [detail]);
  const simulationScenarioRows = useMemo(() => simulationControl?.scenarios || [], [simulationControl]);
  const simulationBatchRows = useMemo(() => simulationControl?.batches || [], [simulationControl]);
  const simulationSafeRecipients = useMemo(() => simulationControl?.outbound_guard?.safe_recipients || [], [simulationControl]);
  const activeIncidentOptions = useMemo(
    () => incidentRows.filter((entry) => ["open", "under_review", "confirmed"].includes(entry.status)),
    [incidentRows]
  );

  const priorityCards = useMemo(() => ([
    {
      key: "requires_immediate_review",
      label: OPERATIONAL_PRIORITY_META.requires_immediate_review.label,
      count: Number(center?.operational_priority_counts?.requires_immediate_review || 0),
      hint: OPERATIONAL_PRIORITY_META.requires_immediate_review.hint,
      color: "danger",
    },
    {
      key: "high_risk_but_contained",
      label: OPERATIONAL_PRIORITY_META.high_risk_but_contained.label,
      count: Number(center?.operational_priority_counts?.high_risk_but_contained || 0),
      hint: OPERATIONAL_PRIORITY_META.high_risk_but_contained.hint,
      color: "warning",
    },
    {
      key: "formal_review_pending",
      label: OPERATIONAL_PRIORITY_META.formal_review_pending.label,
      count: Number(center?.operational_priority_counts?.formal_review_pending || 0),
      hint: OPERATIONAL_PRIORITY_META.formal_review_pending.hint,
      color: "info",
    },
    {
      key: "monitoring_low_priority",
      label: OPERATIONAL_PRIORITY_META.monitoring_low_priority.label,
      count: Number(center?.operational_priority_counts?.monitoring_low_priority || 0),
      hint: OPERATIONAL_PRIORITY_META.monitoring_low_priority.hint,
      color: "secondary",
    },
  ]), [center]);

  const queueCards = useMemo(() => ([
    {
      key: "critical_cases",
      label: "Kritische Employer-Faelle",
      count: Number(center?.queue_counts?.critical_cases || 0),
      hint: "restricted / rejected / hold / investigation",
    },
    {
      key: "open_reviews",
      label: "Offene Reviews",
      count: Number(center?.queue_counts?.open_reviews || 0),
      hint: "review / waiting / recheck",
    },
    {
      key: "incidents_complaints",
      label: "Aktive Incidents / Complaints",
      count: Number(center?.queue_counts?.incidents_complaints || 0),
      hint: "open / confirmed / complaints",
    },
    {
      key: "publish_blocked",
      label: "Publish-blockierte Employer",
      count: Number(center?.queue_counts?.publish_blocked || 0),
      hint: "trust / incident / GPT / recheck",
    },
    {
      key: "auto_paused_ads",
      label: "Auto-paused Ads",
      count: Number(center?.queue_counts?.auto_paused_ads || 0),
      hint: "durch Trust/Fraud automatisch pausiert",
    },
    {
      key: "escalated_cases",
      label: "Escalated Cases",
      count: Number(center?.queue_counts?.escalated_cases || 0),
      hint: "review / urgent / critical",
    },
  ]), [center]);

  const tableRows = useMemo(() => {
    const rows = center?.rows || [];
    if (operationalPriorityFilter === "all") return rows;
    return rows.filter((entry) => entry.operationalPriority === operationalPriorityFilter);
  }, [center, operationalPriorityFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const employerUserid = params.get("employer");
    const notificationId = params.get("notification");
    const simulationBatchId = params.get("simulation_batch");

    if (employerUserid && (!detail?.employer?.employerUserid || detail.employer.employerUserid !== employerUserid) && !loadingDetail) {
      openDetail(employerUserid);
    }

    if (notificationId && notificationRows.length) {
      const nextNotification = notificationRows.find((entry) => String(entry.id) === String(notificationId));
      if (nextNotification) {
        setNotificationDetail(nextNotification);
        setNotificationVisible(true);
      }
    }

    if (simulationBatchId && simulationBatchId !== search) {
      setSearch(simulationBatchId);
    }
  }, [location.search, notificationRows, detail, loadingDetail]);

  const openDetail = async (employerUserid) => {
    try {
      setLoadingDetail(true);
      setDetailVisible(true);
      const nextDetail = await getAdminTrustFraudEmployerDetail(employerUserid);
      setDetail(nextDetail);
      setError("");
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Falldetail konnte nicht geladen werden.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const openNotificationDetail = (notification) => {
    if (!notification) return;
    setNotificationDetail(notification);
    setNotificationVisible(true);
  };

  const goToNotificationCase = async (notification) => {
    if (!notification?.employer_userid) return;
    navigate(`/dashboard/admin/trust-fraud?employer=${encodeURIComponent(notification.employer_userid)}&notification=${encodeURIComponent(notification.id)}`);
    await openDetail(notification.employer_userid);
  };

  const runNotificationAction = async (notification, action) => {
    if (!notification?.id || !action) return;
    try {
      setNotificationBusy(true);
      const response = await updateAdminTrustNotificationStatus(notification.id, { action });
      if (response?.notification) {
        setNotificationDetail(response.notification);
      }
      setNotice(`Signal ${action} wurde gespeichert.`);
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Notification-Status konnte nicht aktualisiert werden.");
    } finally {
      setNotificationBusy(false);
    }
  };

  const acknowledgeInbox = async () => {
    try {
      await acknowledgeAdminTrustFraudInbox();
      setNotice("Inbox-Signale wurden als gesehen markiert.");
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Inbox konnte nicht quittiert werden.");
    }
  };

  const openRecipientModal = (recipient = null) => {
    setRecipientDraft({
      id: recipient?.id || null,
      email: recipient?.email || "",
      notification_scope: recipient?.notification_scope || "trust_fraud",
      notification_level: recipient?.notification_level || "urgent_and_critical",
      receives_instant_alerts: recipient ? Boolean(recipient.receives_instant_alerts) : true,
      receives_daily_digest: recipient ? Boolean(recipient.receives_daily_digest) : true,
      allows_simulation_alerts: recipient ? Boolean(recipient.allows_simulation_alerts) : false,
      is_active: recipient ? Boolean(recipient.is_active) : true,
    });
  };

  const saveRecipient = async () => {
    if (!recipientDraft?.email?.trim()) return;
    try {
      setRecipientBusy(true);
      await saveAdminTrustFraudRecipient({
        id: recipientDraft.id || undefined,
        email: recipientDraft.email,
        notification_scope: recipientDraft.notification_scope,
        notification_level: recipientDraft.notification_level,
        receives_instant_alerts: recipientDraft.receives_instant_alerts,
        receives_daily_digest: recipientDraft.receives_daily_digest,
        allows_simulation_alerts: recipientDraft.allows_simulation_alerts,
        is_active: recipientDraft.is_active,
      });
      setRecipientDraft(null);
      setNotice("Notification-Empfaenger wurde gespeichert.");
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Notification-Empfaenger konnte nicht gespeichert werden.");
    } finally {
      setRecipientBusy(false);
    }
  };

  const toggleRecipientActive = async (recipient) => {
    if (!recipient?.id) return;
    try {
      setRecipientBusy(true);
      await saveAdminTrustFraudRecipient({
        id: recipient.id,
        email: recipient.email,
        notification_scope: recipient.notification_scope,
        notification_level: recipient.notification_level,
        receives_instant_alerts: recipient.receives_instant_alerts,
        receives_daily_digest: recipient.receives_daily_digest,
        allows_simulation_alerts: recipient.allows_simulation_alerts,
        is_active: !recipient.is_active,
      });
      setNotice(`Empfaenger ${recipient.email} wurde ${recipient.is_active ? "deaktiviert" : "aktiviert"}.`);
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Empfaengerstatus konnte nicht aktualisiert werden.");
    } finally {
      setRecipientBusy(false);
    }
  };

  const parseSimulationOverrides = () => {
    const raw = simulationDraft?.overrides_json?.trim();
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (_error) {
      throw new Error("Simulation Overrides muessen valides JSON sein.");
    }
  };

  const runSimulationBatch = async () => {
    try {
      setSimulationBusy(true);
      setCleanupResult(null);
      const selectedScenarioKeys = Array.isArray(simulationDraft.scenario_keys)
        ? simulationDraft.scenario_keys.filter(Boolean)
        : [];
      const response = await runAdminTrustSimulation({
        mode: simulationDraft.mode,
        scenario_set_key: simulationDraft.scenario_set_key || undefined,
        scenario_keys: selectedScenarioKeys,
        scenario_overrides: parseSimulationOverrides(),
        notes: simulationDraft.notes,
      });
      setNotice(`Simulationslauf ${response.batch_id} wurde gestartet und abgeschlossen.`);
      setSimulationDraft((current) => ({ ...current, notes: "", overrides_json: "" }));
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Simulationslauf konnte nicht gestartet werden.");
    } finally {
      setSimulationBusy(false);
    }
  };

  const runSimulationCleanup = async (overrides = {}) => {
    try {
      setCleanupBusy(true);
      const payload = {
        batch_id: overrides.batch_id !== undefined ? overrides.batch_id : cleanupDraft.batch_id,
        scenario_key: overrides.scenario_key !== undefined ? overrides.scenario_key : cleanupDraft.scenario_key,
        cleanup_all: overrides.cleanup_all !== undefined ? overrides.cleanup_all : cleanupDraft.cleanup_all,
        dry_run: overrides.dry_run !== undefined ? overrides.dry_run : cleanupDraft.dry_run,
        notes: overrides.notes !== undefined ? overrides.notes : cleanupDraft.notes,
      };
      const response = await cleanupAdminTrustSimulation(payload);
      setCleanupResult(response);
      setNotice(payload.dry_run ? "Cleanup-Preview wurde erzeugt." : "Simulation-Cleanup wurde ausgefuehrt.");
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Simulation-Cleanup konnte nicht ausgefuehrt werden.");
    } finally {
      setCleanupBusy(false);
    }
  };

  const openActionModal = (action, overrides = {}) => {
    const currentEmployer = detail?.employer || overrides.employer || null;
    setActionDraft({
      action,
      reason: "",
      next_action: "",
      target_admin: overrides.target_admin || "",
      incident_id: overrides.incident_id ? String(overrides.incident_id) : "",
      incident_type: overrides.incident_type || "other",
      severity: overrides.severity || "medium",
      summary: overrides.summary || "",
      impact_confirmed: false,
      employer: currentEmployer,
    });
  };

  const runAction = async () => {
    if (!actionDraft?.action || !detail?.employer?.employerUserid) return;
    try {
      setActionBusy(true);
      const payload = {
        action: actionDraft.action,
        reason: actionDraft.reason,
      };
      if (actionDraft.incident_id) payload.incident_id = Number(actionDraft.incident_id);
      if (actionDraft.next_action) payload.next_action = actionDraft.next_action;
      if (actionDraft.target_admin) payload.target_admin = actionDraft.target_admin;
      if (actionDraft.impact_confirmed) payload.impact_confirmed = true;
      if (actionDraft.action === "complaint_to_incident") {
        payload.incident_type = actionDraft.incident_type;
        payload.severity = actionDraft.severity;
        payload.summary = actionDraft.summary;
      }

      const response = await runAdminTrustFraudAction(detail.employer.employerUserid, payload);
      setDetail(response?.detail || null);
      setActionDraft(null);
      setNotice(`${ACTION_META[payload.action]?.label || payload.action} wurde gespeichert.`);
      setRefreshTick((current) => current + 1);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || "Admin-Aktion konnte nicht ausgefuehrt werden.");
    } finally {
      setActionBusy(false);
    }
  };

  const currentTrust = detail?.trust_summary || detail?.employer?.trustSummary || null;
  const currentEmployer = detail?.employer || null;
  const currentActionMeta = ACTION_META[actionDraft?.action] || null;
  const currentActionIsHighImpact = HIGH_IMPACT_ACTIONS.has(String(actionDraft?.action || ""));
  const currentActionSafetySummary = buildActionSafetySummary(actionDraft?.action, actionDraft?.employer || currentEmployer);

  const actions = (
    <div className="d-flex flex-wrap gap-2">
      <CButton color="secondary" variant="outline" onClick={() => setRefreshTick((current) => current + 1)}>
        Aktualisieren
      </CButton>
      <CButton color="primary" variant="outline" onClick={acknowledgeInbox}>
        Inbox quittieren
      </CButton>
    </div>
  );

  return (
    <AdminPageFrame
      title="Admin Trust & Fraud Center"
      subtitle="Zentrale operative Admin-Wahrheit fuer Trust, Fraud, Misrepresentation, Complaints, Rechecks, GPT-Risiko und betroffene Live-Anzeigen."
      badge="Admin Trust Truth Layer"
      actions={actions}
      contentClassName="d-grid gap-4"
    >
      {error ? (
        <CCallout color="danger">
          <strong>Fehler im Trust & Fraud Center.</strong>
          <div>{error}</div>
        </CCallout>
      ) : null}

      {notice ? (
        <CCallout color="success">
          <strong>Aktion gespeichert.</strong>
          <div>{notice}</div>
        </CCallout>
      ) : null}

      <section className="d-grid gap-3">
        <CCallout color={Number(center?.inbox?.unresolved_critical || 0) > 0 ? "danger" : "info"}>
          <div className="d-flex flex-wrap justify-content-between gap-3">
            <div className="d-grid gap-1">
              <strong>Operative Inbox fuer neue und alternde Trust-/Fraud-Signale</strong>
              <div className="small text-body-secondary">
                Neue seit letztem Besuch, ungelöste kritische Fälle, alternde Problemfälle, Eskalationen und stille Risikofälle laufen hier zusammen.
              </div>
              <div className="d-flex flex-wrap gap-2">
                <CBadge color="info">Neu seit letztem Besuch: {Number(center?.inbox?.new_since_last_visit || 0)}</CBadge>
                <CBadge color="danger">Unresolved critical: {Number(center?.inbox?.unresolved_critical || 0)}</CBadge>
                <CBadge color="warning">Aging unresolved: {Number(center?.inbox?.aging_unresolved || 0)}</CBadge>
                <CBadge color="danger">Escalated: {Number(center?.inbox?.escalated || 0)}</CBadge>
                <CBadge color="warning">Silent but risky: {Number(center?.inbox?.silent_but_risky || 0)}</CBadge>
                <CBadge color="secondary">Acknowledged: {Number(center?.inbox?.acknowledged || 0)}</CBadge>
                <CBadge color="info">In progress: {Number(center?.inbox?.in_progress || 0)}</CBadge>
                <CBadge color="success">Resolved: {Number(center?.inbox?.resolved || 0)}</CBadge>
              </div>
            </div>
            <div className="text-end">
              <div className="small text-body-secondary">Letzter Besuch</div>
              <strong>{formatDateTime(center?.last_viewed_at)}</strong>
            </div>
          </div>
        </CCallout>

        <div className="row g-3">
          <div className="col-md-6 col-xl-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
              <div className="text-uppercase small text-body-secondary">Problematische Employer</div>
              <strong className="fs-3">{Number(center?.totals?.employers || 0)}</strong>
              <div className="small text-body-secondary">Zentral sichtbar im Admin Trust & Fraud Center.</div>
            </div>
          </div>
          <div className="col-md-6 col-xl-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
              <div className="text-uppercase small text-body-secondary">Betroffene Live-Anzeigen</div>
              <strong className="fs-3">{Number(center?.totals?.live_ads_affected || 0)}</strong>
              <div className="small text-body-secondary">Aktive Reichweite mit operativem Trust-/Fraud-Risiko.</div>
            </div>
          </div>
          <div className="col-md-6 col-xl-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
              <div className="text-uppercase small text-body-secondary">Escalated Cases</div>
              <strong className="fs-3">{Number(center?.totals?.escalated_cases || 0)}</strong>
              <div className="small text-body-secondary">Review, urgent oder critical eskaliert.</div>
            </div>
          </div>
          <div className="col-md-6 col-xl-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
              <div className="text-uppercase small text-body-secondary">Severe Impact</div>
              <strong className="fs-3">{Number(center?.totals?.severe_impact_cases || 0)}</strong>
              <div className="small text-body-secondary">Faelle mit hoher Reichweite oder Schadensnaehe.</div>
            </div>
          </div>
        </div>
        <CCallout color={Number(center?.inbox?.deltas?.new_critical_cases || 0) > 0 ? "danger" : "info"}>
          <strong>Delta seit letztem Besuch</strong>
          <div className="mt-2 small">
            Neue kritische Faelle {Number(center?.inbox?.deltas?.new_critical_cases || 0)} · neue Eskalationen {Number(center?.inbox?.deltas?.new_escalations || 0)} · alte ungelöste Fälle {Number(center?.inbox?.deltas?.old_unresolved_cases || 0)} · neu betroffene Live-Anzeigen {Number(center?.inbox?.deltas?.newly_affected_live_ads || 0)} · neu betroffene Bewerber {Number(center?.inbox?.deltas?.newly_affected_applicants || 0)}
          </div>
        </CCallout>
        <CCallout color={Number(center?.enforcement_summary?.sla_breached_critical || 0) > 0 ? "danger" : "warning"}>
          <strong>Enforcement & Accountability</strong>
          <div className="mt-2 small">
            Kritische SLA-Verstoesse {Number(center?.enforcement_summary?.sla_breached_critical || 0)} · urgent/critical ohne Owner {Number(center?.enforcement_summary?.urgent_critical_without_owner || 0)} · re-raised acknowledged {Number(center?.enforcement_summary?.reraised_acknowledged || 0)} · neglected Cases {Number(center?.enforcement_summary?.neglected_cases || 0)} · severe impact offen {Number(center?.enforcement_summary?.severe_impact_open || 0)}
          </div>
        </CCallout>
        <CCallout color={outboundSummary?.delivery_state === "ready" ? (Number(outboundSummary?.last_24h?.failed || 0) > 0 ? "warning" : "info") : "warning"}>
          <strong>External Escalation & Outbound Mail</strong>
          <div className="mt-2 small">
            Zustand {outboundSummary?.delivery_state || "disabled"} · aktive Empfaenger {Number(outboundSummary?.active_recipient_count || 0)} · Instant {Number(outboundSummary?.instant_recipient_count || 0)} · Digest {Number(outboundSummary?.digest_recipient_count || 0)} · letzte 24h pending {Number(outboundSummary?.last_24h?.pending || 0)} · sent {Number(outboundSummary?.last_24h?.sent || 0)} · failed {Number(outboundSummary?.last_24h?.failed || 0)} · skipped {Number(outboundSummary?.last_24h?.skipped || 0)}
          </div>
          <div className="mt-2 small">
            Letzter Digest {formatDateTime(outboundSummary?.last_digest?.sent_at || outboundSummary?.last_digest?.created_at)}
          </div>
        </CCallout>
      </section>

      <section className="d-grid gap-3">
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div>
            <div className="text-uppercase small text-body-secondary">Controlled Simulation Framework</div>
            <h3 className="h4 mb-0">Kontrollierte Trust-/Fraud-Stresstests</h3>
          </div>
          <div className="small text-body-secondary">
            Aktive Simulationsfaelle {Number(simulationControl?.current?.employers || 0)} · sichere Mail-Empfaenger {Number(simulationControl?.outbound_guard?.simulation_safe_recipient_count || 0)}
          </div>
        </div>

        <CCallout color={simulationDraft.mode === "delivery" && Number(simulationControl?.outbound_guard?.simulation_safe_recipient_count || 0) === 0 ? "warning" : "info"}>
          <strong>Simulation ist isoliert und cleanup-faehig.</strong>
          <div className="mt-2 small">
            Structural Simulation erzeugt nur interne Faelle. Delivery Simulation queued Outbound nur fuer simulation-safe Empfaenger und markiert jede Mail sichtbar als Simulation.
          </div>
        </CCallout>

        <div className="row g-3">
          <div className="col-lg-7">
            <div className="rounded border border-slate-200 bg-white p-3 h-100 d-grid gap-3">
              <div className="row g-3">
                <div className="col-md-4">
                  <CFormLabel htmlFor="admin-trust-simulation-mode">Modus</CFormLabel>
                  <CFormSelect
                    id="admin-trust-simulation-mode"
                    value={simulationDraft.mode}
                    options={SIMULATION_MODE_OPTIONS}
                    onChange={(event) => setSimulationDraft((current) => ({ ...current, mode: event.target.value }))}
                  />
                </div>
                <div className="col-md-8">
                  <CFormLabel htmlFor="admin-trust-simulation-set">Scenario Set</CFormLabel>
                  <CFormSelect
                    id="admin-trust-simulation-set"
                    value={simulationDraft.scenario_set_key}
                    onChange={(event) => setSimulationDraft((current) => ({ ...current, scenario_set_key: event.target.value }))}
                    options={[
                      { value: "", label: "Custom Selection" },
                      ...((simulationControl?.scenario_sets || []).map((entry) => ({ value: entry.key, label: entry.label }))),
                    ]}
                  />
                </div>
              </div>

              <div>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                  <CFormLabel className="mb-0">Referenzszenarien</CFormLabel>
                  <small className="text-body-secondary">Mehrfachauswahl fuer kontrollierte Batch-Laeufe</small>
                </div>
                <div className="row g-2">
                  {simulationScenarioRows.map((scenario) => {
                    const selected = simulationDraft.scenario_set_key
                      ? false
                      : simulationDraft.scenario_keys.includes(scenario.key);
                    return (
                      <div className="col-md-6" key={scenario.key}>
                        <div className="rounded border border-slate-200 bg-slate-50 p-2 h-100">
                          <CFormCheck
                            id={`simulation-scenario-${scenario.key}`}
                            checked={selected}
                            disabled={Boolean(simulationDraft.scenario_set_key)}
                            onChange={(event) => {
                              setSimulationDraft((current) => ({
                                ...current,
                                scenario_keys: event.target.checked
                                  ? [...current.scenario_keys, scenario.key]
                                  : current.scenario_keys.filter((entry) => entry !== scenario.key),
                              }));
                            }}
                            label={scenario.label}
                          />
                          <div className="small text-body-secondary mt-1">{scenario.description}</div>
                          <div className="mt-2 d-flex flex-wrap gap-1">
                            <CBadge color="secondary">{scenario.category}</CBadge>
                            <CBadge color="dark">{scenario.key}</CBadge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <CFormLabel htmlFor="admin-trust-simulation-overrides">Override JSON (optional)</CFormLabel>
                <CFormTextarea
                  id="admin-trust-simulation-overrides"
                  rows={4}
                  value={simulationDraft.overrides_json}
                  onChange={(event) => setSimulationDraft((current) => ({ ...current, overrides_json: event.target.value }))}
                  placeholder='{"defaults":{"live_ads_count":2},"critical_aging_auto_escalation":{"ownership_state":"unassigned"}}'
                />
                <div className="small text-body-secondary mt-1">
                  Unterstuetzt u. a. employer_type, employer_classification, trust_tier, risk_level, domain_state, website_state, mandate_state, contact_state, review_state, incident_type, live_ads_count, affected_applications_count, gpt_risk_signature, enforcement_state, escalation_state, ownership_state.
                </div>
              </div>

              <div>
                <CFormLabel htmlFor="admin-trust-simulation-notes">Notizen</CFormLabel>
                <CFormTextarea
                  id="admin-trust-simulation-notes"
                  rows={2}
                  value={simulationDraft.notes}
                  onChange={(event) => setSimulationDraft((current) => ({ ...current, notes: event.target.value }))}
                  placeholder="Zweck des Laufes, Testhypothese oder Operator-Kontext"
                />
              </div>

              <div className="d-flex flex-wrap gap-2">
                <CLoadingButton color="dark" loading={simulationBusy} onClick={runSimulationBatch}>
                  Simulationslauf starten
                </CLoadingButton>
                <CLoadingButton color="secondary" variant="outline" loading={cleanupBusy} onClick={() => runSimulationCleanup({ dry_run: true })}>
                  Cleanup Preview
                </CLoadingButton>
                <CLoadingButton color="danger" variant="outline" loading={cleanupBusy} onClick={() => runSimulationCleanup({ dry_run: false })}>
                  Cleanup ausfuehren
                </CLoadingButton>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="rounded border border-slate-200 bg-white p-3 h-100 d-grid gap-3">
              <div className="row g-3">
                <div className="col-6">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
                    <div className="text-uppercase small text-body-secondary">Simulation Employers</div>
                    <strong className="fs-4">{Number(simulationControl?.current?.employers || 0)}</strong>
                  </div>
                </div>
                <div className="col-6">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
                    <div className="text-uppercase small text-body-secondary">Simulation Notifications</div>
                    <strong className="fs-4">{Number(simulationControl?.current?.active_notifications || 0)}</strong>
                  </div>
                </div>
                <div className="col-6">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
                    <div className="text-uppercase small text-body-secondary">Simulation Outbound</div>
                    <strong className="fs-4">{Number(simulationControl?.current?.outbound_deliveries || 0)}</strong>
                  </div>
                </div>
                <div className="col-6">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100">
                    <div className="text-uppercase small text-body-secondary">Safe Recipients</div>
                    <strong className="fs-4">{simulationSafeRecipients.length}</strong>
                  </div>
                </div>
              </div>

              <CCallout color={simulationDraft.mode === "delivery" && simulationSafeRecipients.length === 0 ? "danger" : "info"}>
                <strong>Outbound-Schutz</strong>
                <div className="mt-2 small">
                  Delivery-State {simulationControl?.outbound_guard?.delivery_state || "disabled"} · Simulation-safe Empfaenger {simulationSafeRecipients.length}. Ohne simulation-safe Empfaenger bleibt Delivery Simulation intern bzw. skipped.
                </div>
              </CCallout>

              <div>
                <CFormLabel htmlFor="admin-trust-cleanup-batch">Cleanup by Batch ID</CFormLabel>
                <CFormInput
                  id="admin-trust-cleanup-batch"
                  value={cleanupDraft.batch_id}
                  onChange={(event) => setCleanupDraft((current) => ({ ...current, batch_id: event.target.value }))}
                  placeholder="sim-..."
                />
              </div>
              <div>
                <CFormLabel htmlFor="admin-trust-cleanup-scenario">Cleanup by Scenario Key</CFormLabel>
                <CFormInput
                  id="admin-trust-cleanup-scenario"
                  value={cleanupDraft.scenario_key}
                  onChange={(event) => setCleanupDraft((current) => ({ ...current, scenario_key: event.target.value }))}
                  placeholder="critical_aging_auto_escalation"
                />
              </div>
              <div className="d-grid gap-2">
                <CFormCheck
                  id="admin-trust-cleanup-all"
                  checked={Boolean(cleanupDraft.cleanup_all)}
                  onChange={(event) => setCleanupDraft((current) => ({ ...current, cleanup_all: event.target.checked }))}
                  label="Alle Simulationsdaten bereinigen"
                />
                <CFormCheck
                  id="admin-trust-cleanup-dry-run"
                  checked={Boolean(cleanupDraft.dry_run)}
                  onChange={(event) => setCleanupDraft((current) => ({ ...current, dry_run: event.target.checked }))}
                  label="Dry Run"
                />
              </div>
              {cleanupResult ? (
                <CCallout color={cleanupResult.dry_run ? "warning" : "success"}>
                  <strong>{cleanupResult.dry_run ? "Cleanup Preview" : "Cleanup Ergebnis"}</strong>
                  <div className="mt-2 small">
                    Batches {(cleanupResult.batch_ids || []).join(", ") || "-"} · Szenarien {(cleanupResult.scenario_keys || []).join(", ") || "-"}
                  </div>
                  <div className="mt-2 small">
                    {formatJson(cleanupResult.preview || cleanupResult.deleted || {})}
                  </div>
                </CCallout>
              ) : null}
            </div>
          </div>
        </div>

        <CSmartTable
          columns={[
            { key: "batch", label: "Batch" },
            { key: "mode", label: "Mode / Status" },
            { key: "scope", label: "Szenarien" },
            { key: "counts", label: "Effekte" },
            { key: "created", label: "Erstellt" },
            { key: "actions", label: "Aktionen", sorter: false, filter: false },
          ]}
          items={simulationBatchRows}
          pagination={false}
          sorter={false}
          tableProps={{ hover: true, responsive: true }}
          scopedColumns={{
            batch: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.batch_id}</strong>
                  <small className="text-body-secondary">{item.notes || "-"}</small>
                </div>
              </td>
            ),
            mode: (item) => (
              <td>
                <div className="d-flex flex-wrap gap-1">
                  <CBadge color={item.mode === "delivery" ? "warning" : "info"}>{item.mode}</CBadge>
                  <CBadge color={item.status === "completed" ? "success" : item.status === "failed" ? "danger" : item.status.includes("clean") ? "secondary" : "warning"}>{item.status}</CBadge>
                  <CBadge color="dark">{item.outbound_mode}</CBadge>
                </div>
              </td>
            ),
            scope: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.scenario_set_key || "custom_selection"}</strong>
                  <small className="text-body-secondary">{(item.scenario_keys || []).join(", ") || "-"}</small>
                </div>
              </td>
            ),
            counts: (item) => (
              <td>
                <div className="small">
                  Employer {item.employer_count} · Ads {item.ad_count} · Bewerbungen {item.application_count} · Incidents {item.incident_count} · Notifications {item.notification_count} · Outbound {item.outbound_count}
                </div>
              </td>
            ),
            created: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <span>{formatDateTime(item.created_at)}</span>
                  <small className="text-body-secondary">{item.simulation_created_by || "-"}</small>
                </div>
              </td>
            ),
            actions: (item) => (
              <td>
                <div className="d-flex flex-wrap gap-2">
                  <CButton color="secondary" size="sm" variant="outline" onClick={() => setSearch(item.batch_id)}>
                    Im Center filtern
                  </CButton>
                  <CLoadingButton
                    color="warning"
                    size="sm"
                    variant="outline"
                    loading={cleanupBusy}
                    onClick={() => {
                      setCleanupDraft((current) => ({ ...current, batch_id: item.batch_id, scenario_key: "", cleanup_all: false, dry_run: true }));
                      runSimulationCleanup({ batch_id: item.batch_id, scenario_key: "", cleanup_all: false, dry_run: true });
                    }}
                  >
                    Preview
                  </CLoadingButton>
                  <CLoadingButton
                    color="danger"
                    size="sm"
                    variant="outline"
                    loading={cleanupBusy}
                    onClick={() => {
                      setCleanupDraft((current) => ({ ...current, batch_id: item.batch_id, scenario_key: "", cleanup_all: false, dry_run: false }));
                      runSimulationCleanup({ batch_id: item.batch_id, scenario_key: "", cleanup_all: false, dry_run: false });
                    }}
                  >
                    Cleanup
                  </CLoadingButton>
                </div>
              </td>
            ),
          }}
        />
      </section>

      <section className="d-grid gap-3">
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div>
            <div className="text-uppercase small text-body-secondary">Notification-Empfaenger</div>
            <h3 className="h4 mb-0">Aktive Outbound-Verteiler fuer Trust & Fraud</h3>
          </div>
          <CButton color="primary" variant="outline" onClick={() => openRecipientModal()}>
            Empfaenger hinzufuegen
          </CButton>
        </div>

        <CSmartTable
          columns={[
            { key: "email", label: "Empfaenger" },
            { key: "scope", label: "Scope / Level" },
            { key: "delivery", label: "Instant / Digest" },
            { key: "status", label: "Status" },
            { key: "updated", label: "Aktualisiert" },
            { key: "actions", label: "Aktionen", sorter: false, filter: false },
          ]}
          items={recipientRows}
          pagination={false}
          sorter={false}
          tableProps={{ hover: true, responsive: true }}
          scopedColumns={{
            email: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.email}</strong>
                  <small className="text-body-secondary">Erstellt von {item.created_by || "-"}</small>
                </div>
              </td>
            ),
            scope: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <CBadge color="info">{item.notification_scope}</CBadge>
                  <small className="text-body-secondary">{item.notification_level}</small>
                </div>
              </td>
            ),
            delivery: (item) => (
              <td>
                <div className="d-flex flex-wrap gap-1">
                  <CBadge color={item.receives_instant_alerts ? "success" : "secondary"}>{item.receives_instant_alerts ? "Instant an" : "Instant aus"}</CBadge>
                  <CBadge color={item.receives_daily_digest ? "info" : "secondary"}>{item.receives_daily_digest ? "Digest an" : "Digest aus"}</CBadge>
                  <CBadge color={item.allows_simulation_alerts ? "warning" : "secondary"}>{item.allows_simulation_alerts ? "Simulation-safe" : "Keine Simulation"}</CBadge>
                </div>
              </td>
            ),
            status: (item) => (
              <td>
                <CBadge color={item.is_active ? "success" : "secondary"}>{item.is_active ? "Aktiv" : "Inaktiv"}</CBadge>
              </td>
            ),
            updated: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <span>{formatDateTime(item.updated_at)}</span>
                  <small className="text-body-secondary">{item.updated_by || "-"}</small>
                </div>
              </td>
            ),
            actions: (item) => (
              <td>
                <div className="d-flex flex-wrap gap-2">
                  <CButton color="secondary" size="sm" variant="outline" onClick={() => openRecipientModal(item)}>
                    Bearbeiten
                  </CButton>
                  <CLoadingButton color={item.is_active ? "warning" : "success"} size="sm" variant="outline" loading={recipientBusy} onClick={() => toggleRecipientActive(item)}>
                    {item.is_active ? "Deaktivieren" : "Aktivieren"}
                  </CLoadingButton>
                </div>
              </td>
            ),
          }}
        />
      </section>

      <section className="d-grid gap-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className="text-uppercase small text-body-secondary">Notification Inbox</div>
            <h3 className="h4 mb-0">Aktive und geklaerte Trust-/Fraud-Signale</h3>
          </div>
          <div className="small text-body-secondary">{notificationRows.length} Signale im System</div>
        </div>

        <CSmartTable
          columns={[
            { key: "notification", label: "Signal" },
            { key: "employer", label: "Employer" },
            { key: "status", label: "Status / SLA" },
            { key: "impact", label: "Impact / Escalation" },
            { key: "whyNow", label: "Why now" },
            { key: "actions", label: "Aktionen", sorter: false, filter: false },
          ]}
          items={notificationRows}
          pagination={false}
          sorter={false}
          tableProps={{ hover: true, responsive: true }}
          scopedColumns={{
            notification: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.notification_type_label || item.notification_type}</strong>
                  <small className="text-body-secondary">{item.short_summary || "-"}</small>
                  <div className="d-flex flex-wrap gap-1">
                    <SeverityBadge severity={item.severity} />
                    <EscalationBadge value={item.escalation_level} />
                    <EnforcementBadge value={item.enforcement_category} />
                    {item.simulation_flag ? <CBadge color="dark">Simulation</CBadge> : null}
                  </div>
                </div>
              </td>
            ),
            employer: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.employer_userid || "-"}</strong>
                  <small className="text-body-secondary">{item.affected_live_ads} Live-Anzeigen · {item.affected_applicants} Bewerber</small>
                  <div className="d-flex flex-wrap gap-1">
                    <OwnershipBadge value={item.ownership_state} />
                    {item.current_owner_admin ? <CBadge color="dark">{item.current_owner_admin}</CBadge> : null}
                  </div>
                </div>
              </td>
            ),
            status: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <div className="d-flex flex-wrap gap-1">
                    <NotificationStatusBadge value={item.status} />
                    <SlaBadge value={item.sla_class} />
                  </div>
                  <small className="text-body-secondary">Erkannt {formatDateTime(item.first_detected_at)}</small>
                  <small className="text-body-secondary">Zuletzt aktiv {formatDateTime(item.last_detected_at)}</small>
                  {item.reraised_acknowledged ? <small className="text-danger">Acknowledged wurde ohne Fortschritt wieder hochgezogen.</small> : null}
                </div>
              </td>
            ),
            impact: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <ImpactBadge value={item.impact_level} />
                  <small className="text-body-secondary">Prioritaet {OPERATIONAL_PRIORITY_META[item.operational_priority]?.label || item.operational_priority}</small>
                  <small className="text-body-secondary">Age {formatAge({ ageHours: item.age_hours, ageDays: item.age_days })}</small>
                  {item.owner_gap ? <small className="text-danger">Owner-Gap bei operativem Risiko.</small> : null}
                </div>
              </td>
            ),
            whyNow: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.why_now || "-"}</strong>
                  {item.delta?.isNew ? <small className="text-info">Neu seit letztem Besuch</small> : null}
                  {item.delta?.isEscalated ? <small className="text-danger">Neu eskaliert</small> : null}
                  {Array.isArray(item.enforcement_triggers) && item.enforcement_triggers.length ? (
                    <small className="text-body-secondary">
                      Trigger: {item.enforcement_triggers.slice(0, 2).map((entry) => entry.label).join(" · ")}
                    </small>
                  ) : null}
                </div>
              </td>
            ),
            actions: (item) => (
              <td>
                <div className="d-flex flex-wrap gap-2">
                  <CButton color="primary" size="sm" variant="outline" onClick={() => openNotificationDetail(item)}>
                    Signal-Detail
                  </CButton>
                  <CButton color="secondary" size="sm" variant="outline" onClick={() => goToNotificationCase(item)}>
                    Zum Fall
                  </CButton>
                  {item.status === "open" ? (
                    <CButton color="warning" size="sm" variant="outline" onClick={() => runNotificationAction(item, "acknowledge")}>
                      Acknowledge
                    </CButton>
                  ) : null}
                  {item.status !== "in_progress" && item.status !== "resolved" ? (
                    <CButton color="info" size="sm" variant="outline" onClick={() => runNotificationAction(item, "mark_in_progress")}>
                      In Progress
                    </CButton>
                  ) : null}
                  {item.status !== "resolved" ? (
                    <CButton color="success" size="sm" variant="outline" onClick={() => runNotificationAction(item, "mark_resolved")}>
                      Resolve
                    </CButton>
                  ) : (
                    <CButton color="danger" size="sm" variant="outline" onClick={() => runNotificationAction(item, "reopen")}>
                      Reopen
                    </CButton>
                  )}
                </div>
              </td>
            ),
          }}
        />
      </section>

      <section className="d-grid gap-3">
        <div>
          <div className="text-uppercase small text-body-secondary">Dominante Hauptsicht</div>
          <h3 className="h4 mb-1">Operative Prioritaet</h3>
          <div className="small text-body-secondary">Diese Fläche trennt akute Eingriffsfälle von formalen Review-Blockern, bevor du überhaupt in technische Queues gehst.</div>
        </div>
        <div className="row g-3">
          {priorityCards.map((card) => (
            <div key={card.key} className="col-md-6 col-xl-3">
              <PriorityLaneCard
                active={operationalPriorityFilter === card.key}
                label={card.label}
                count={card.count}
                hint={card.hint}
                color={card.color}
                onClick={() => setOperationalPriorityFilter((current) => (current === card.key ? "all" : card.key))}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="d-grid gap-3">
        <div className="row g-3">
          {queueCards.map((card) => (
            <div key={card.key} className="col-md-6 col-xl">
              <QueueCard
                active={queueFilter === card.key}
                label={card.label}
                count={card.count}
                hint={card.hint}
                onClick={() => setQueueFilter((current) => (current === card.key ? "all" : card.key))}
              />
            </div>
          ))}
        </div>

        <div className="row g-3 align-items-end">
          <div className="col-lg-4">
            <CFormLabel htmlFor="admin-trust-search">Employer oder Signal suchen</CFormLabel>
            <CFormInput
              id="admin-trust-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Employer, Flag, Queue oder Trust-Signal"
            />
          </div>
          <div className="col-md-6 col-lg-3">
            <CFormLabel htmlFor="admin-trust-queue">Queue</CFormLabel>
            <CFormSelect
              id="admin-trust-queue"
              value={queueFilter}
              options={QUEUE_OPTIONS}
              onChange={(event) => setQueueFilter(event.target.value)}
            />
          </div>
          <div className="col-md-6 col-lg-3">
            <CFormLabel htmlFor="admin-trust-priority">Prioritaet</CFormLabel>
            <CFormSelect
              id="admin-trust-priority"
              value={priorityFilter}
              options={PRIORITY_OPTIONS}
              onChange={(event) => setPriorityFilter(event.target.value)}
            />
          </div>
          <div className="col-lg-2">
            <CButton color="secondary" variant="outline" className="w-100" onClick={() => {
              setQueueFilter("all");
              setPriorityFilter("all");
              setOperationalPriorityFilter("all");
              setSearch("");
            }}>
              Filter reset
            </CButton>
          </div>
        </div>
      </section>

      <section className="d-grid gap-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <div className="text-uppercase small text-body-secondary">Zentrale Queue</div>
            <h3 className="h4 mb-0">Priorisierte Employer-Faelle</h3>
          </div>
          {loading ? <CSpinner size="sm" /> : <div className="small text-body-secondary">{tableRows.length} Faelle im aktuellen Filter</div>}
        </div>

        <CSmartTable
          columns={[
            { key: "priority", label: "Operative Prioritaet" },
            { key: "employer", label: "Employer-Fall" },
            { key: "impact", label: "Noise / Impact / Eskalation" },
            { key: "trust", label: "Trust / Risk" },
            { key: "review", label: "Review / SLA / Owner" },
            { key: "incidents", label: "Incidents / Reichweite / Enforcement" },
            { key: "gpt", label: "GPT Erklaerung / Fokus" },
            { key: "actions", label: "Aktionen", sorter: false, filter: false },
          ]}
          items={tableRows}
          loading={loading}
          pagination={false}
          sorter={false}
          tableProps={{ hover: true, responsive: true }}
          scopedColumns={{
            priority: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <OperationalPriorityBadge value={item.operationalPriority} />
                  <NoiseClassBadge value={item.noiseClass} />
                  <PriorityBadge level={item.priority} />
                  <small className="text-body-secondary">Priority Score {Number(item.priorityScore || 0)}</small>
                  <small className="text-body-secondary">{item.operationalPriorityLabel || "-"}</small>
                </div>
              </td>
            ),
            employer: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>{item.name}</strong>
                  <small className="text-body-secondary">{item.employerUserid}</small>
                  <div className="d-flex flex-wrap gap-1">
                    <CBadge color="secondary">{formatEmployerTypeLabel(item.employerType)}</CBadge>
                    <CBadge color="secondary" className="text-wrap">{formatEmployerClassificationLabel(item.employerClassification)}</CBadge>
                    {item.simulationFlag ? <CBadge color="dark">Simulation</CBadge> : null}
                  </div>
                  {item.representedFacilityName ? (
                    <small className="text-body-secondary">Mandat / Einrichtung: {item.representedFacilityName}</small>
                  ) : item.facilityName ? (
                    <small className="text-body-secondary">Einrichtung: {item.facilityName}</small>
                  ) : null}
                </div>
              </td>
            ),
            impact: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <div className="d-flex flex-wrap gap-1">
                    <ImpactBadge value={item.impactLevel} />
                    <EscalationBadge value={item.escalationLevel} />
                    <EnforcementBadge value={item.enforcementCategory} />
                    {item.simulationBatchId ? <CBadge color="dark">{item.simulationBatchId}</CBadge> : null}
                  </div>
                  <small className="text-body-secondary">Impact Score {Number(item.impactScore || 0)}</small>
                  <small className="text-body-secondary">{(item.impactReasons || []).slice(0, 2).join(" · ") || "-"}</small>
                  {item.escalationReason ? (
                    <small className="text-danger">{item.escalationReason}</small>
                  ) : (
                    <small className="text-body-secondary">Keine aktive Eskalation.</small>
                  )}
                </div>
              </td>
            ),
            trust: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <CBadge color={getEmployerTrustStatusColor(item.trustStatus)}>{item.trustStatusLabel}</CBadge>
                  <div className="d-flex flex-wrap gap-1">
                    <CBadge color={getTrustTierColor(item.trustTier)}>{formatTrustTierLabel(item.trustTier)}</CBadge>
                    <CBadge color={getRiskLevelColor(item.riskLevel)}>{formatRiskLevelLabel(item.riskLevel)}</CBadge>
                    <CBadge color={getPublishingRightsStageColor(item.publishingRightsStage)}>{formatPublishingRightsStageLabel(item.publishingRightsStage)}</CBadge>
                  </div>
                  {item.publishingHold || item.underInvestigation ? (
                    <small className="text-danger">
                      {item.publishingHold ? "Publishing-Hold" : ""}
                      {item.publishingHold && item.underInvestigation ? " · " : ""}
                      {item.underInvestigation ? "Under investigation" : ""}
                    </small>
                  ) : null}
                </div>
              </td>
            ),
            review: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <CBadge color={getTrustReviewWorkflowStatusColor(item.reviewWorkflowStatus)}>
                    {formatTrustReviewWorkflowStatusLabel(item.reviewWorkflowStatus)}
                  </CBadge>
                  <small className="text-body-secondary">
                    {item.reviewTaskBlockers} blockierende Tasks · Recheck {formatBoolean(item.trustRecheckRequired)}
                  </small>
                  <small className="text-body-secondary">{item.repeatedRecheckCount} wiederholte Rechecks</small>
                  <div className="d-flex flex-wrap gap-1">
                    <OwnershipBadge value={item.ownershipState} />
                    {item.currentOwnerAdmin ? <CBadge color="dark">{item.currentOwnerAdmin}</CBadge> : null}
                  </div>
                  {item.ownerGap ? <small className="text-danger">Owner fehlt trotz operativem Druck.</small> : null}
                  <small className={item.aging?.unresolvedTooLong || item.aging?.reviewStalled || item.aging?.investigationStalled || item.aging?.waitingOnEmployerTooLong ? "text-danger" : "text-body-secondary"}>
                    {item.aging?.referenceLabel || "Fall offen"} · {formatAge(item.aging)}
                  </small>
                </div>
              </td>
            ),
            incidents: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <div className="d-flex flex-wrap gap-1">
                    <CBadge color={item.activeIncidentCount ? "danger" : "secondary"}>
                      {item.activeIncidentCount} aktiv
                    </CBadge>
                    <CBadge color={item.criticalIncidentCount ? "danger" : "secondary"}>
                      {item.criticalIncidentCount} kritisch
                    </CBadge>
                  </div>
                  <small className="text-body-secondary">
                    {item.complaintCount} Complaints{item.activeComplaintFlag ? " · active applicant_complaint_flag" : ""}
                  </small>
                  <small className="text-body-secondary">
                    {item.liveAdsCount} live · {item.affectedLiveAdsCount} live betroffen · {item.affectedApplicationsCount} Bewerber
                  </small>
                  {item.enforcementTriggerCount ? (
                    <small className="text-danger">
                      {item.enforcementTriggerCount} Enforcement-Trigger · {(item.enforcementTriggers || []).slice(0, 2).map((entry) => entry.label).join(" · ")}
                    </small>
                  ) : null}
                </div>
              </td>
            ),
            gpt: (item) => (
              <td>
                <div className="d-grid gap-1">
                  <strong>Plausibility {formatPercent(item.realityPlausibilityScore)} · Risk {formatPercent(item.realityRiskScore)}</strong>
                  <small className="text-body-secondary">{(item.gptExplainability?.topRiskDrivers || []).slice(0, 2).join(" · ") || "Keine dominanten Treiber"}</small>
                  <small className="text-body-secondary">{item.gptExplainability?.recommendedAdminFocus || item.nextAction || "-"}</small>
                </div>
              </td>
            ),
            actions: (item) => (
              <td>
                <div className="d-flex flex-wrap gap-2">
                  <CButton color="primary" size="sm" variant="outline" onClick={() => openDetail(item.employerUserid)}>
                    Falldetail
                  </CButton>
                  {item.operationalPriority === "requires_immediate_review" ? (
                    <CButton color="danger" size="sm" variant="outline" onClick={() => openDetail(item.employerUserid)}>
                      Sofort pruefen
                    </CButton>
                  ) : null}
                </div>
              </td>
            ),
          }}
        />

        {!loading && !tableRows.length ? (
          <CCallout color="info">
            <strong>Keine Employer-Faelle im aktuellen Filter.</strong>
            <div>Die Queue bleibt streng problemorientiert. Wenn sie leer ist, gibt es aktuell keine sichtbaren Trust-/Fraud-Arbeitsfaelle im gewaehlten Filter.</div>
          </CCallout>
        ) : null}
      </section>
      <COffcanvas
        placement="end"
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        style={{ width: "min(96vw, 1080px)" }}
      >
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>Admin Trust & Fraud Falldetail</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          {loadingDetail ? (
            <div className="py-5 d-flex justify-content-center">
              <CSpinner />
            </div>
          ) : currentEmployer && currentTrust ? (
            <div className="d-grid gap-4">
              <CCallout color={currentEmployer.operationalPriority === "requires_immediate_review" ? "danger" : currentEmployer.operationalPriority === "high_risk_but_contained" ? "warning" : "info"}>
                <div className="d-flex flex-wrap justify-content-between gap-3">
                  <div>
                    <strong>{currentEmployer.name}</strong>
                    <div>{currentEmployer.nextAction}</div>
                    <div className="small text-body-secondary mt-1">
                      Letzte Aktivitaet {formatDateTime(currentEmployer.lastActivityAt)} · {currentEmployer.employerUserid}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-start">
                    <OperationalPriorityBadge value={currentEmployer.operationalPriority} />
                    <NoiseClassBadge value={currentEmployer.noiseClass} />
                    <ImpactBadge value={currentEmployer.impactLevel} />
                    <EscalationBadge value={currentEmployer.escalationLevel} />
                    <PriorityBadge level={currentEmployer.priority} />
                    <CBadge color={getEmployerTrustStatusColor(currentEmployer.trustStatus)}>{currentEmployer.trustStatusLabel}</CBadge>
                    <CBadge color={getRiskLevelColor(currentEmployer.riskLevel)}>{currentEmployer.riskLevelLabel}</CBadge>
                    {detail?.simulation?.flag ? <CBadge color="dark">Simulation</CBadge> : null}
                  </div>
                </div>
              </CCallout>

              {detail?.simulation?.flag ? (
                <CCallout color="dark">
                  <strong>Kontrollierter Simulationsfall</strong>
                  <div className="mt-2 small">
                    Batch {detail.simulation.batch_id || "-"} · Szenario {detail.simulation.scenario_key || "-"} · erzeugt von {detail.simulation.created_by || "-"} am {formatDateTime(detail.simulation.created_at)}
                  </div>
                  <div className="mt-2 small">
                    {detail.simulation.notes || "Dieser Employer-Fall ist systemisch als Simulation markiert und kein echter Live-Employer."}
                  </div>
                </CCallout>
              ) : null}

              <div className="row g-3">
                <div className="col-xl-4">
                  <CCallout color={currentEmployer.escalationLevel === "critical" ? "danger" : currentEmployer.escalationLevel === "urgent" ? "warning" : "info"} className="h-100 mb-0">
                    <strong>Eskalation</strong>
                    <div className="mt-2">{currentEmployer.escalationReason || "Keine aktive Eskalation."}</div>
                    <div className="small text-body-secondary mt-2">
                      Level {currentEmployer.escalationLevel || "none"} · seit {formatDateTime(currentEmployer.escalatedAt)}
                    </div>
                  </CCallout>
                </div>
                <div className="col-xl-4">
                  <CCallout color={currentEmployer.impactLevel === "severe" ? "danger" : currentEmployer.impactLevel === "high" ? "warning" : "info"} className="h-100 mb-0">
                    <strong>Operativer Impact</strong>
                    <div className="mt-2">Impact Score {Number(currentEmployer.impactScore || 0)} · {currentEmployer.impactLevel || "low"}</div>
                    <div className="small text-body-secondary mt-2">
                      {currentEmployer.affectedLiveAdsCount} betroffene Live-Anzeigen · {currentEmployer.affectedApplicationsCount} betroffene Bewerber
                    </div>
                    <div className="small text-body-secondary">{(currentEmployer.impactReasons || []).join(" · ") || "Kein harter Impact-Treiber."}</div>
                  </CCallout>
                </div>
                <div className="col-xl-4">
                  <CCallout color={currentEmployer.aging?.unresolvedTooLong || currentEmployer.aging?.investigationStalled || currentEmployer.aging?.reviewStalled ? "danger" : currentEmployer.aging?.waitingOnEmployerTooLong ? "warning" : "info"} className="h-100 mb-0">
                    <strong>Aging / SLA</strong>
                    <div className="mt-2">{currentEmployer.aging?.referenceLabel || "Fall offen"} · {formatAge(currentEmployer.aging)}</div>
                    <div className="small text-body-secondary mt-2">
                      Review stalled {formatBoolean(currentEmployer.aging?.reviewStalled)} · Investigation stalled {formatBoolean(currentEmployer.aging?.investigationStalled)}
                    </div>
                    <div className="small text-body-secondary">
                      Waiting on employer too long {formatBoolean(currentEmployer.aging?.waitingOnEmployerTooLong)} · Unresolved too long {formatBoolean(currentEmployer.aging?.unresolvedTooLong)}
                    </div>
                  </CCallout>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-xl-6">
                  <CCallout color={detail?.ownership?.owner_gap ? "danger" : "info"} className="h-100 mb-0">
                    <strong>Ownership & Accountability</strong>
                    <div className="mt-2 d-flex flex-wrap gap-1">
                      <OwnershipBadge value={detail?.ownership?.state} />
                      {detail?.ownership?.current_owner_admin ? <CBadge color="dark">{detail.ownership.current_owner_admin}</CBadge> : null}
                    </div>
                    <div className="small text-body-secondary mt-2">
                      First seen {formatDateTime(detail?.accountability?.first_seen_at)} durch {detail?.accountability?.first_seen_by || "-"}
                    </div>
                    <div className="small text-body-secondary">
                      Last progressed {formatDateTime(detail?.accountability?.last_progressed_at)} durch {detail?.accountability?.last_progressed_by || "-"}
                    </div>
                    <div className="small text-body-secondary">
                      Reopen {Number(detail?.accountability?.reopen_count || 0)} · Enforcement {Number(detail?.accountability?.enforcement_count || 0)} · unresolved {Number(detail?.accountability?.total_time_unresolved_hours || 0)}h
                    </div>
                    {detail?.ownership?.owner_gap ? (
                      <div className="small text-danger mt-2">Kritischer oder reichweitenstarker Fall ohne primaeren Owner.</div>
                    ) : null}
                  </CCallout>
                </div>
                <div className="col-xl-6">
                  <CCallout color={detail?.enforcement?.sla_breached || detail?.enforcement?.auto_escalated ? "danger" : "warning"} className="h-100 mb-0">
                    <strong>Enforcement</strong>
                    <div className="mt-2 d-flex flex-wrap gap-1">
                      <EnforcementBadge value={detail?.enforcement?.category} />
                      {detail?.enforcement?.auto_escalated ? <CBadge color="danger">Auto Escalated</CBadge> : null}
                      {detail?.enforcement?.re_raised_acknowledged ? <CBadge color="warning">Re-raised acknowledged</CBadge> : null}
                    </div>
                    <div className="small text-body-secondary mt-2">
                      Trigger {Number(detail?.enforcement?.trigger_count || 0)} · SLA breach {formatBoolean(detail?.enforcement?.sla_breached)} · last enforcement {formatDateTime(detail?.enforcement?.last_enforcement_transition_at)}
                    </div>
                    <div className="small text-body-secondary">
                      Auto escalation reason: {detail?.enforcement?.auto_escalation_reason || "-"}
                    </div>
                    {(detail?.enforcement?.triggers || []).length ? (
                      <div className="mt-2 d-flex flex-wrap gap-1">
                        {detail.enforcement.triggers.map((entry) => (
                          <CBadge key={entry.key} color={entry.severity === "critical" ? "danger" : entry.severity === "high" ? "warning" : "info"}>
                            {entry.label}
                          </CBadge>
                        ))}
                      </div>
                    ) : null}
                  </CCallout>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <CButton color="primary" variant="outline" onClick={() => openActionModal("start_review")}>Review starten</CButton>
                <CButton color="warning" variant="outline" onClick={() => openActionModal("set_waiting_for_employer")}>Waiting for employer</CButton>
                <CButton color="warning" variant="outline" onClick={() => openActionModal("set_waiting_for_evidence")}>Waiting for evidence</CButton>
                <CButton color="success" variant="outline" onClick={() => openActionModal("complete_review_verified")}>Review verified</CButton>
                <CButton color="danger" variant="outline" onClick={() => openActionModal("complete_review_rejected")}>Review rejected</CButton>
                <CButton color="danger" variant="outline" onClick={() => openActionModal("set_restricted")}>Restricted</CButton>
                <CButton
                  color={currentEmployer.underInvestigation ? "secondary" : "danger"}
                  variant="outline"
                  onClick={() => openActionModal(currentEmployer.underInvestigation ? "clear_under_investigation" : "set_under_investigation")}
                >
                  {currentEmployer.underInvestigation ? "Untersuchung beenden" : "Under investigation"}
                </CButton>
                <CButton
                  color={currentEmployer.publishingHold ? "secondary" : "danger"}
                  variant="outline"
                  onClick={() => openActionModal(currentEmployer.publishingHold ? "release_publishing_hold" : "set_publishing_hold")}
                >
                  {currentEmployer.publishingHold ? "Publishing-Hold aufheben" : "Publishing-Hold setzen"}
                </CButton>
                <CButton color="secondary" variant="outline" onClick={() => openActionModal("trigger_recheck")}>Recheck ausloesen</CButton>
                <CButton color="danger" variant="outline" onClick={() => openActionModal("pause_affected_ads")}>Anzeigen pausieren</CButton>
                <CButton color="success" variant="outline" onClick={() => openActionModal("release_affected_ads")}>Anzeigen freigeben</CButton>
                <CButton color="info" variant="outline" onClick={() => openActionModal("complaint_to_incident")}>Complaint -> Incident</CButton>
                <CButton color="info" variant="outline" onClick={() => openActionModal("assign_to_me")}>Mir zuweisen</CButton>
                <CButton color="secondary" variant="outline" onClick={() => openActionModal("assign_to_admin")}>Admin zuweisen</CButton>
                <CButton color="warning" variant="outline" onClick={() => openActionModal("handover_case")}>Fall uebergeben</CButton>
                <CButton color="secondary" variant="outline" onClick={() => openActionModal("release_owner")}>Owner loesen</CButton>
              </div>

              <div className="row g-3">
                <div className="col-xl-8">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <DetailField label="Employer Type" value={formatEmployerTypeLabel(currentEmployer.employerType)} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Employer Classification" value={formatEmployerClassificationLabel(currentEmployer.employerClassification)} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Trust Tier" value={formatTrustTierLabel(currentEmployer.trustTier)} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Publishing Rights Stage" value={formatPublishingRightsStageLabel(currentEmployer.publishingRightsStage)} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Trust Status" value={currentEmployer.trustStatusLabel} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Risk Level" value={`${formatRiskLevelLabel(currentEmployer.riskLevel)} (${formatPercent(currentEmployer.riskScore)})`} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Operative Prioritaet" value={currentEmployer.operationalPriorityLabel || "-"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Noise Class" value={currentEmployer.noiseClassLabel || "-"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Impact" value={`${currentEmployer.impactLevel || "low"} (${Number(currentEmployer.impactScore || 0)})`} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Escalation" value={currentEmployer.escalationLevel || "none"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Ownership" value={`${OWNERSHIP_META[currentEmployer.ownershipState || "unassigned"]?.label || currentEmployer.ownershipState || "unassigned"}${currentEmployer.currentOwnerAdmin ? ` · ${currentEmployer.currentOwnerAdmin}` : ""}`} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Enforcement" value={ENFORCEMENT_META[currentEmployer.enforcementCategory || "standard_enforcement"]?.label || currentEmployer.enforcementCategory || "-"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Under investigation" value={formatBoolean(currentEmployer.underInvestigation)} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Publishing hold" value={formatBoolean(currentEmployer.publishingHold)} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Time to first ack" value={detail?.accountability?.time_to_first_ack != null ? `${detail.accountability.time_to_first_ack}h` : "-"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Time to in progress" value={detail?.accountability?.time_to_in_progress != null ? `${detail.accountability.time_to_in_progress}h` : "-"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Represented facility / mandate" value={currentEmployer.representedFacilityName || currentEmployer.facilityName || "-"} />
                    </div>
                    <div className="col-md-6">
                      <DetailField label="Mandatskontext" value={detail?.profile?.represented_facility_proof || "-"} />
                    </div>
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100 d-grid gap-2">
                    <div className="text-uppercase small text-body-secondary">Automatische Systemreaktionen</div>
                    {(detail.system_reactions || []).length ? (
                      <ul className="mb-0 ps-3">
                        {detail.system_reactions.map((entry) => (
                          <li key={entry}>{entry}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-body-secondary">Keine aktiven Systemreaktionen.</div>
                    )}
                    <div className="small text-body-secondary">
                      Naechste empfohlene Admin-Aktion: <strong>{detail.next_admin_action || "-"}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-xl-7">
                  <CCallout color={currentEmployer.gptCriticalFlags?.length ? "danger" : currentTrust.realityCheck?.requiresReview ? "warning" : "info"}>
                    <strong>GPT Reality Check</strong>
                    <div className="mt-2">
                      Plausibility {formatPercent(currentEmployer.realityPlausibilityScore)} · Risk {formatPercent(currentEmployer.realityRiskScore)} · letzter Check {formatDateTime(currentEmployer.realityLastCheckedAt)}
                    </div>
                    <div className="mt-2"><strong>Evidence Summary:</strong> {detail?.gpt_explainability?.evidenceSummary || currentEmployer.realitySummary || "Noch kein aussagekraeftiger GPT-Reality-Check vorliegend."}</div>
                    <div className="mt-2"><strong>Contradiction Summary:</strong> {detail?.gpt_explainability?.contradictionSummary || "Keine harte Widerspruchserklaerung verfuegbar."}</div>
                    <div className="mt-2"><strong>Recommended Admin Focus:</strong> {detail?.gpt_explainability?.recommendedAdminFocus || currentEmployer.realityRecommendation || "-"}</div>
                    {(detail?.gpt_explainability?.topRiskDrivers || []).length ? (
                      <div className="mt-2 d-flex flex-wrap gap-1">
                        {detail.gpt_explainability.topRiskDrivers.map((entry) => (
                          <CBadge key={entry} color="warning">{entry}</CBadge>
                        ))}
                      </div>
                    ) : null}
                    {(currentEmployer.gptCriticalFlags || []).length ? (
                      <div className="mt-2 d-flex flex-wrap gap-1">
                        {currentEmployer.gptCriticalFlags.map((flag) => (
                          <CBadge key={flag} color="danger">{formatEmployerTrustFlagLabel(flag)}</CBadge>
                        ))}
                      </div>
                    ) : null}
                  </CCallout>
                </div>
                <div className="col-xl-5">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 h-100 d-grid gap-2">
                    <div className="text-uppercase small text-body-secondary">Behavior Risk</div>
                    <strong>{formatPercent(currentEmployer.behaviorRiskScore)}</strong>
                    <div className="text-body-secondary">
                      Suspicious activity flag: {formatBoolean(currentEmployer.suspiciousActivityFlag)}
                    </div>
                    <div className="text-body-secondary">
                      Complaints {currentEmployer.complaintCount} · aktive Incidents {currentEmployer.activeIncidentCount} · kritische Incidents {currentEmployer.criticalIncidentCount}
                    </div>
                    <div className="text-body-secondary">
                      Live Ads {currentEmployer.liveAdsCount} · auto-paused {currentEmployer.autoPausedAdsCount} · betroffene Bewerber {currentEmployer.affectedApplicationsCount}
                    </div>
                    <div className="text-body-secondary">
                      Escalation {currentEmployer.escalationLevel || "none"} · Impact {currentEmployer.impactLevel || "low"} · Aging {formatAge(currentEmployer.aging)}
                    </div>
                  </div>
                </div>
              </div>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Enforcement Trigger</h3>
                {(detail?.enforcement?.triggers || []).length ? (
                  <div className="d-grid gap-2">
                    {detail.enforcement.triggers.map((entry) => (
                      <CCallout key={entry.key} color={entry.severity === "critical" ? "danger" : entry.severity === "high" ? "warning" : "info"}>
                        <strong>{entry.label}</strong>
                        <div className="mt-1">{entry.reason}</div>
                      </CCallout>
                    ))}
                  </div>
                ) : (
                  <CCallout color="info">
                    <strong>Keine aktiven Enforcement-Trigger.</strong>
                    <div>Aktuell feuert kein zusaetzlicher Drucktrigger ueber dem Basis-Trust-/Fraud-Signal.</div>
                  </CCallout>
                )}
              </section>

              <section className="d-grid gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="h5 mb-0">Offene Review Areas</h3>
                  <CBadge color={getTrustReviewWorkflowStatusColor(currentEmployer.reviewWorkflowStatus)}>
                    {formatTrustReviewWorkflowStatusLabel(currentEmployer.reviewWorkflowStatus)}
                  </CBadge>
                </div>
                <CSmartTable
                  columns={[
                    { key: "areaLabel", label: "Bereich" },
                    { key: "reviewStatusLabel", label: "Review-Status" },
                    { key: "reviewNotes", label: "Notizen" },
                    { key: "reviewedAt", label: "Geprueft am" },
                    { key: "reviewedBy", label: "Geprueft von" },
                  ]}
                  items={reviewRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    reviewStatusLabel: (item) => (
                      <td><CBadge color={item.reviewStatusColor}>{item.reviewStatusLabel}</CBadge></td>
                    ),
                    reviewNotes: (item) => <td>{item.reviewNotes || "-"}</td>,
                    reviewedAt: (item) => <td>{formatDateTime(item.reviewedAt)}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Offene Review Tasks</h3>
                <CSmartTable
                  columns={[
                    { key: "title", label: "Task" },
                    { key: "statusLabel", label: "Status" },
                    { key: "blocking", label: "Blockierend" },
                    { key: "nextAction", label: "Naechste Aktion" },
                    { key: "notes", label: "Notizen" },
                    { key: "dueAt", label: "Faelligkeit" },
                  ]}
                  items={taskRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    statusLabel: (item) => (
                      <td><CBadge color={item.statusColor}>{item.statusLabel}</CBadge></td>
                    ),
                    dueAt: (item) => <td>{formatDateTime(item.dueAt)}</td>,
                    notes: (item) => <td>{item.notes || "-"}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Aktive Incidents / Complaints</h3>
                <CSmartTable
                  columns={[
                    { key: "typeLabel", label: "Incident" },
                    { key: "severity", label: "Severity" },
                    { key: "statusLabel", label: "Status" },
                    { key: "summary", label: "Summary" },
                    { key: "reportedBy", label: "Reported by" },
                    { key: "reportedAt", label: "Reported at" },
                    { key: "actions", label: "Aktionen", sorter: false, filter: false },
                  ]}
                  items={incidentRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    severity: (item) => <td><SeverityBadge severity={item.severity} /></td>,
                    statusLabel: (item) => <td><CBadge color={item.statusColor}>{item.statusLabel}</CBadge></td>,
                    summary: (item) => <td>{item.summary || "-"}</td>,
                    reportedAt: (item) => <td>{formatDateTime(item.reportedAt)}</td>,
                    actions: (item) => (
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          {item.status !== "confirmed" ? (
                            <CButton color="danger" size="sm" variant="outline" onClick={() => openActionModal("confirm_incident", { incident_id: item.id })}>
                              Bestaetigen
                            </CButton>
                          ) : null}
                          {item.status !== "rejected" ? (
                            <CButton color="secondary" size="sm" variant="outline" onClick={() => openActionModal("reject_incident", { incident_id: item.id })}>
                              Zurueckweisen
                            </CButton>
                          ) : null}
                          {item.status !== "resolved" ? (
                            <CButton color="success" size="sm" variant="outline" onClick={() => openActionModal("resolve_incident", { incident_id: item.id })}>
                              Geloest
                            </CButton>
                          ) : null}
                        </div>
                      </td>
                    ),
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Complaint-Historie</h3>
                <CSmartTable
                  columns={[
                    { key: "severity", label: "Severity" },
                    { key: "status", label: "Status" },
                    { key: "reported_at", label: "Gemeldet am" },
                    { key: "summary", label: "Beschwerdeinhalt" },
                    { key: "action_taken", label: "Aktion" },
                  ]}
                  items={detail?.complaint_history || []}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    severity: (item) => <td><SeverityBadge severity={item.severity} /></td>,
                    status: (item) => <td><CBadge color={getTrustIncidentStatusColor(item.status)}>{formatTrustIncidentStatusLabel(item.status)}</CBadge></td>,
                    reported_at: (item) => <td>{formatDateTime(item.reported_at)}</td>,
                    summary: (item) => <td>{item.summary || "-"}</td>,
                    action_taken: (item) => <td>{item.action_taken || "-"}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Verknuepfte Notifications</h3>
                <CSmartTable
                  columns={[
                    { key: "notification_type_label", label: "Signal" },
                    { key: "status", label: "Status" },
                    { key: "severity", label: "Severity" },
                    { key: "ownership", label: "Owner / Enforcement" },
                    { key: "why_now", label: "Why now" },
                    { key: "last_detected_at", label: "Zuletzt aktiv" },
                    { key: "actions", label: "Aktionen", sorter: false, filter: false },
                  ]}
                  items={detail?.related_notifications || []}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    status: (item) => <td><NotificationStatusBadge value={item.status} /></td>,
                    severity: (item) => <td><SeverityBadge severity={item.severity} /></td>,
                    ownership: (item) => (
                      <td>
                        <div className="d-grid gap-1">
                          <div className="d-flex flex-wrap gap-1">
                            <OwnershipBadge value={item.ownership_state} />
                            <EnforcementBadge value={item.enforcement_category} />
                          </div>
                          <small className="text-body-secondary">{item.current_owner_admin || "Kein Owner"}</small>
                          {item.reraised_acknowledged ? <small className="text-danger">Re-raised acknowledged</small> : null}
                        </div>
                      </td>
                    ),
                    why_now: (item) => <td>{item.why_now || "-"}</td>,
                    last_detected_at: (item) => <td>{formatDateTime(item.last_detected_at)}</td>,
                    actions: (item) => (
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <CButton color="primary" size="sm" variant="outline" onClick={() => openNotificationDetail(item)}>
                            Signal-Detail
                          </CButton>
                          <CButton color="secondary" size="sm" variant="outline" onClick={() => goToNotificationCase(item)}>
                            Zum Fall
                          </CButton>
                        </div>
                      </td>
                    ),
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Outbound Mailzustellung</h3>
                {detail?.last_outbound_delivery ? (
                  <CCallout color={detail.last_outbound_delivery.status === "failed" ? "danger" : detail.last_outbound_delivery.status === "sent" ? "success" : "info"}>
                    <strong>Letzter Versand</strong>
                    <div className="mt-2 small">
                      {formatOutboundTypeLabel(detail.last_outbound_delivery.notification_type)} · {detail.last_outbound_delivery.recipient_email} · {detail.last_outbound_delivery.status} · {formatDateTime(detail.last_outbound_delivery.sent_at || detail.last_outbound_delivery.created_at)}
                    </div>
                    {detail.last_outbound_delivery.simulation_flag ? (
                      <div className="mt-2 small text-body-secondary">
                        Simulation Batch {detail.last_outbound_delivery.simulation_batch_id || "-"} · Szenario {detail.last_outbound_delivery.simulation_scenario_key || "-"} · simulation-safe {formatBoolean(detail.last_outbound_delivery.recipient_allows_simulation_alerts)}
                      </div>
                    ) : null}
                    {detail.last_outbound_delivery.error_text ? (
                      <div className="mt-2 small text-danger">{detail.last_outbound_delivery.error_text}</div>
                    ) : null}
                  </CCallout>
                ) : (
                  <CCallout color="info">
                    <strong>Noch kein Outbound-Mailversand sichtbar.</strong>
                    <div>Fuer diesen Employer-Fall wurde bisher noch kein Trust-/Fraud-Alert nach aussen zugestellt.</div>
                  </CCallout>
                )}
                <CSmartTable
                  columns={[
                    { key: "notification_type", label: "Alert" },
                    { key: "recipient_email", label: "Empfaenger" },
                    { key: "status", label: "Status" },
                    { key: "impact", label: "Impact / Escalation" },
                    { key: "created_at", label: "Erzeugt" },
                    { key: "sent_at", label: "Gesendet" },
                    { key: "error_text", label: "Fehler / Skip" },
                  ]}
                  items={outboundDeliveryRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    notification_type: (item) => (
                      <td>
                        <div className="d-grid gap-1">
                          <span>{formatOutboundTypeLabel(item.notification_type)}</span>
                          {item.simulation_flag ? <CBadge color="dark">Simulation</CBadge> : null}
                        </div>
                      </td>
                    ),
                    recipient_email: (item) => (
                      <td>
                        <div className="d-grid gap-1">
                          <span>{item.recipient_email}</span>
                          <small className="text-body-secondary">{item.recipient_allows_simulation_alerts ? "simulation-safe" : "keine Simulation"}</small>
                        </div>
                      </td>
                    ),
                    status: (item) => <td><OutboundStatusBadge value={item.status} /></td>,
                    impact: (item) => (
                      <td>
                        <div className="d-grid gap-1">
                          <ImpactBadge value={item.impact_level} />
                          <small className="text-body-secondary">Escalation {item.escalation_level || "none"}</small>
                        </div>
                      </td>
                    ),
                    created_at: (item) => <td>{formatDateTime(item.created_at)}</td>,
                    sent_at: (item) => <td>{formatDateTime(item.sent_at || item.last_attempt_at)}</td>,
                    error_text: (item) => <td>{item.error_text || "-"}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Betroffene aktive Anzeigen</h3>
                <CSmartTable
                  columns={[
                    { key: "title", label: "Anzeige" },
                    { key: "status", label: "Status" },
                    { key: "applicationCount", label: "Bewerber" },
                    { key: "location", label: "Standort" },
                    { key: "lastTrustPauseAt", label: "Letzte Trust-Pause" },
                    { key: "trustPauseAction", label: "Pause-Ursprung" },
                    { key: "previousStatus", label: "Vorheriger Status" },
                  ]}
                  items={detail?.affected_ads || []}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    title: (item) => (
                      <td>
                        <div className="d-grid gap-1">
                          <strong>{item.title}</strong>
                          <small className="text-body-secondary">{item.role || "-"}</small>
                        </div>
                      </td>
                    ),
                    applicationCount: (item) => <td>{Number(item.applicationCount || 0)}</td>,
                    lastTrustPauseAt: (item) => <td>{formatDateTime(item.lastTrustPauseAt || item.lastAutoPausedAt)}</td>,
                    trustPauseAction: (item) => <td>{item.trustPauseAction || "-"}</td>,
                    previousStatus: (item) => <td>{item.previousStatus || "-"}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Operative Timeline</h3>
                <CSmartTable
                  columns={[
                    { key: "occurredAt", label: "Zeit" },
                    { key: "title", label: "Ereignis" },
                    { key: "summary", label: "Zusammenfassung" },
                    { key: "severity", label: "Severity" },
                    { key: "actor", label: "Actor" },
                  ]}
                  items={timelineRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    occurredAt: (item) => <td>{formatDateTime(item.occurredAt)}</td>,
                    summary: (item) => <td>{item.summary || "-"}</td>,
                    severity: (item) => <td><SeverityBadge severity={item.severity} /></td>,
                    actor: (item) => <td>{item.actor || "system"}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">Trust History</h3>
                <CSmartTable
                  columns={[
                    { key: "eventType", label: "Event" },
                    { key: "reason", label: "Reason" },
                    { key: "changedBy", label: "Actor" },
                    { key: "changedAt", label: "Timestamp" },
                  ]}
                  items={historyRows}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    changedAt: (item) => <td>{formatDateTime(item.changedAt)}</td>,
                    reason: (item) => <td>{item.reason || "-"}</td>,
                  }}
                />
              </section>

              <section className="d-grid gap-2">
                <h3 className="h5 mb-0">History Payloads / Audit</h3>
                <CSmartTable
                  columns={[
                    { key: "event_type", label: "Event" },
                    { key: "old_values", label: "Alte Werte" },
                    { key: "new_values", label: "Neue Werte" },
                    { key: "changed_by", label: "Actor" },
                    { key: "changed_at", label: "Zeit" },
                  ]}
                  items={detail?.history || []}
                  pagination={false}
                  sorter={false}
                  tableProps={{ hover: true, responsive: true }}
                  scopedColumns={{
                    old_values: (item) => <td>{formatJson(item.old_values)}</td>,
                    new_values: (item) => <td>{formatJson(item.new_values)}</td>,
                    changed_at: (item) => <td>{formatDateTime(item.changed_at)}</td>,
                  }}
                />
              </section>
            </div>
          ) : (
            <CCallout color="info">
              <strong>Kein Employer-Fall geladen.</strong>
              <div>Bitte einen Eintrag aus der zentralen Queue oeffnen.</div>
            </CCallout>
          )}
        </COffcanvasBody>
      </COffcanvas>

      <COffcanvas
        placement="end"
        visible={notificationVisible}
        onHide={() => setNotificationVisible(false)}
        style={{ width: "min(92vw, 720px)" }}
      >
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>Notification-Detail</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          {notificationDetail ? (
            <div className="d-grid gap-4">
              <CCallout color={notificationDetail.severity === "critical" ? "danger" : notificationDetail.severity === "high" ? "warning" : "info"}>
                <strong>{notificationDetail.notification_type_label || notificationDetail.notification_type}</strong>
                <div className="mt-2">{notificationDetail.short_summary || "-"}</div>
                <div className="small text-body-secondary mt-2">
                  Employer {notificationDetail.employer_userid} · {notificationDetail.affected_live_ads} Live-Anzeigen · {notificationDetail.affected_applicants} Bewerber
                </div>
                {notificationDetail.simulation_flag ? (
                  <div className="small text-body-secondary mt-2">
                    Simulation Batch {notificationDetail.simulation_batch_id || "-"} · Szenario {notificationDetail.simulation_scenario_key || "-"}
                  </div>
                ) : null}
              </CCallout>

              <div className="row g-3">
                <div className="col-md-6">
                  <DetailField label="Status" value={NOTIFICATION_STATUS_META[notificationDetail.status]?.label || notificationDetail.status} />
                </div>
                <div className="col-md-6">
                  <DetailField label="SLA" value={SLA_META[notificationDetail.sla_class]?.label || notificationDetail.sla_class} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Severity" value={String(notificationDetail.severity || "-").toUpperCase()} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Escalation" value={notificationDetail.escalation_level || "none"} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Impact" value={notificationDetail.impact_level || "low"} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Operative Prioritaet" value={OPERATIONAL_PRIORITY_META[notificationDetail.operational_priority]?.label || notificationDetail.operational_priority} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Ownership" value={`${OWNERSHIP_META[notificationDetail.ownership_state || "unassigned"]?.label || notificationDetail.ownership_state || "unassigned"}${notificationDetail.current_owner_admin ? ` · ${notificationDetail.current_owner_admin}` : ""}`} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Enforcement" value={ENFORCEMENT_META[notificationDetail.enforcement_category || "standard_enforcement"]?.label || notificationDetail.enforcement_category || "-"} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Erstellt" value={formatDateTime(notificationDetail.created_at)} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Zuletzt aktiv" value={formatDateTime(notificationDetail.last_detected_at)} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Acknowledged" value={formatDateTime(notificationDetail.acknowledged_at)} />
                </div>
                <div className="col-md-6">
                  <DetailField label="Resolved" value={formatDateTime(notificationDetail.resolved_at)} />
                </div>
              </div>

              <CCallout color="warning">
                <strong>Why now</strong>
                <div className="mt-2">{notificationDetail.why_now || "-"}</div>
                {(notificationDetail.enforcement_triggers || []).length ? (
                  <div className="mt-2 small">
                    Trigger: {notificationDetail.enforcement_triggers.map((entry) => entry.label).join(" · ")}
                  </div>
                ) : null}
                {notificationDetail.reraised_acknowledged ? (
                  <div className="mt-2 small text-danger">Acknowledged reicht hier nicht mehr als operative Endstation; der Fall wurde wieder hochgezogen.</div>
                ) : null}
              </CCallout>

              <div className="d-flex flex-wrap gap-2">
                <CButton color="secondary" variant="outline" onClick={() => goToNotificationCase(notificationDetail)}>
                  Zum Employer-Fall
                </CButton>
                {notificationDetail.status === "open" ? (
                  <CLoadingButton color="warning" variant="outline" loading={notificationBusy} onClick={() => runNotificationAction(notificationDetail, "acknowledge")}>
                    Acknowledge
                  </CLoadingButton>
                ) : null}
                {notificationDetail.status !== "in_progress" && notificationDetail.status !== "resolved" ? (
                  <CLoadingButton color="info" variant="outline" loading={notificationBusy} onClick={() => runNotificationAction(notificationDetail, "mark_in_progress")}>
                    In Progress
                  </CLoadingButton>
                ) : null}
                {notificationDetail.status !== "resolved" ? (
                  <CLoadingButton color="success" variant="outline" loading={notificationBusy} onClick={() => runNotificationAction(notificationDetail, "mark_resolved")}>
                    Resolve
                  </CLoadingButton>
                ) : (
                  <CLoadingButton color="danger" variant="outline" loading={notificationBusy} onClick={() => runNotificationAction(notificationDetail, "reopen")}>
                    Reopen
                  </CLoadingButton>
                )}
              </div>
            </div>
          ) : (
            <CCallout color="info">
              <strong>Kein Signal gewaehlt.</strong>
              <div>Bitte ein Notification-Signal aus der Inbox oder aus dem Falldetail oeffnen.</div>
            </CCallout>
          )}
        </COffcanvasBody>
      </COffcanvas>

      <CModal visible={Boolean(recipientDraft)} onClose={() => setRecipientDraft(null)}>
        <CModalHeader>
          <CModalTitle>{recipientDraft?.id ? "Notification-Empfaenger bearbeiten" : "Notification-Empfaenger hinzufuegen"}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {recipientDraft ? (
            <div className="d-grid gap-3">
              <div>
                <CFormLabel htmlFor="admin-trust-recipient-email">E-Mail</CFormLabel>
                <CFormInput
                  id="admin-trust-recipient-email"
                  value={recipientDraft.email}
                  onChange={(event) => setRecipientDraft((current) => ({ ...current, email: event.target.value }))}
                  placeholder="name@example.com"
                />
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <CFormLabel htmlFor="admin-trust-recipient-scope">Scope</CFormLabel>
                  <CFormSelect
                    id="admin-trust-recipient-scope"
                    value={recipientDraft.notification_scope}
                    onChange={(event) => setRecipientDraft((current) => ({ ...current, notification_scope: event.target.value }))}
                    options={RECIPIENT_SCOPE_OPTIONS}
                  />
                </div>
                <div className="col-md-6">
                  <CFormLabel htmlFor="admin-trust-recipient-level">Empfangslevel</CFormLabel>
                  <CFormSelect
                    id="admin-trust-recipient-level"
                    value={recipientDraft.notification_level}
                    onChange={(event) => setRecipientDraft((current) => ({ ...current, notification_level: event.target.value }))}
                    options={RECIPIENT_LEVEL_OPTIONS}
                  />
                </div>
              </div>
              <div className="d-grid gap-2">
                <CFormCheck
                  id="admin-trust-recipient-active"
                  checked={Boolean(recipientDraft.is_active)}
                  onChange={(event) => setRecipientDraft((current) => ({ ...current, is_active: event.target.checked }))}
                  label="Empfaenger aktiv"
                />
                <CFormCheck
                  id="admin-trust-recipient-instant"
                  checked={Boolean(recipientDraft.receives_instant_alerts)}
                  onChange={(event) => setRecipientDraft((current) => ({ ...current, receives_instant_alerts: event.target.checked }))}
                  label="Instant-Alerts empfangen"
                />
                <CFormCheck
                  id="admin-trust-recipient-digest"
                  checked={Boolean(recipientDraft.receives_daily_digest)}
                  onChange={(event) => setRecipientDraft((current) => ({ ...current, receives_daily_digest: event.target.checked }))}
                  label="Daily Digest empfangen"
                />
                <CFormCheck
                  id="admin-trust-recipient-simulation"
                  checked={Boolean(recipientDraft.allows_simulation_alerts)}
                  onChange={(event) => setRecipientDraft((current) => ({ ...current, allows_simulation_alerts: event.target.checked }))}
                  label="Simulation-Alerts ausdruecklich erlauben"
                />
              </div>
            </div>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setRecipientDraft(null)}>
            Abbrechen
          </CButton>
          <CLoadingButton color="primary" loading={recipientBusy} disabled={!recipientDraft?.email?.trim()} onClick={saveRecipient}>
            Empfaenger speichern
          </CLoadingButton>
        </CModalFooter>
      </CModal>

      <CModal visible={Boolean(actionDraft)} onClose={() => setActionDraft(null)}>
        <CModalHeader>
          <CModalTitle>{ACTION_META[actionDraft?.action]?.label || "Admin-Aktion"}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {actionDraft ? (
            <div className="d-grid gap-3">
              <CCallout color={currentActionIsHighImpact ? "danger" : "warning"}>
                <strong>{actionDraft.employer?.name || detail?.employer?.name || "Employer-Fall"}</strong>
                <div>{currentActionMeta?.description || "Diese Aktion wirkt direkt auf Trust, Fraud, Publishing und Incident-Logik."}</div>
                {currentActionIsHighImpact ? (
                  <div className="mt-2 small">
                    High-Impact-Aktion mit {Number(actionDraft.employer?.affectedLiveAdsCount || 0)} betroffenen Live-Anzeigen und {Number(actionDraft.employer?.affectedApplicationsCount || 0)} betroffenen Bewerbern.
                  </div>
                ) : null}
              </CCallout>

              {currentActionIsHighImpact ? (
                <CCallout color="danger">
                  <strong>Impact Warning</strong>
                  <div className="mt-2">
                    Diese Aktion kann Reichweite, Sichtbarkeit oder Untersuchungsstatus sofort veraendern.
                  </div>
                  <div className="mt-2 small">
                    Live-Anzeigen {Number(actionDraft.employer?.affectedLiveAdsCount || 0)} · Bewerber {Number(actionDraft.employer?.affectedApplicationsCount || 0)} · Impact {actionDraft.employer?.impactLevel || "low"} · Escalation {actionDraft.employer?.escalationLevel || "none"}
                  </div>
                  {currentActionSafetySummary ? (
                    <div className="mt-2 small">
                      <div>{currentActionSafetySummary.before}</div>
                      <div>{currentActionSafetySummary.after}</div>
                    </div>
                  ) : null}
                  {currentEmployer?.hasCriticalEnforcementTrigger ? (
                    <div className="mt-2 small text-danger">
                      Kritische Enforcement-Trigger aktiv: {Number(currentEmployer?.enforcementTriggerCount || 0)}.
                    </div>
                  ) : null}
                </CCallout>
              ) : null}

              {currentActionMeta?.requiresTargetAdmin ? (
                <div>
                  <CFormLabel htmlFor="admin-trust-target-admin">Ziel-Admin</CFormLabel>
                  <CFormInput
                    id="admin-trust-target-admin"
                    value={actionDraft.target_admin}
                    onChange={(event) => setActionDraft((current) => ({ ...current, target_admin: event.target.value }))}
                    placeholder="z. B. EU0001ADMIN"
                  />
                </div>
              ) : null}

              {currentActionMeta?.requiresIncident ? (
                <div>
                  <CFormLabel htmlFor="admin-trust-incident">Incident</CFormLabel>
                  <CFormSelect
                    id="admin-trust-incident"
                    value={actionDraft.incident_id}
                    onChange={(event) => setActionDraft((current) => ({ ...current, incident_id: event.target.value }))}
                    options={[
                      { value: "", label: "Incident waehlen" },
                      ...activeIncidentOptions.map((entry) => ({
                        value: String(entry.id),
                        label: `${entry.typeLabel} · ${entry.statusLabel} · ${entry.summary || `#${entry.id}`}`,
                      })),
                    ]}
                  />
                </div>
              ) : null}

              {currentActionMeta?.supportsNextAction ? (
                <div>
                  <CFormLabel htmlFor="admin-trust-next-action">Naechste Aktion / Hinweis an Employer</CFormLabel>
                  <CFormInput
                    id="admin-trust-next-action"
                    value={actionDraft.next_action}
                    onChange={(event) => setActionDraft((current) => ({ ...current, next_action: event.target.value }))}
                    placeholder="z. B. Nachweis der Publishing Authority nachreichen"
                  />
                </div>
              ) : null}

              {currentActionMeta?.supportsComplaintConversion ? (
                <>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <CFormLabel htmlFor="admin-trust-incident-type">Incident-Typ</CFormLabel>
                      <CFormSelect
                        id="admin-trust-incident-type"
                        value={actionDraft.incident_type}
                        onChange={(event) => setActionDraft((current) => ({ ...current, incident_type: event.target.value }))}
                        options={INCIDENT_TYPE_OPTIONS}
                      />
                    </div>
                    <div className="col-md-6">
                      <CFormLabel htmlFor="admin-trust-incident-severity">Severity</CFormLabel>
                      <CFormSelect
                        id="admin-trust-incident-severity"
                        value={actionDraft.severity}
                        onChange={(event) => setActionDraft((current) => ({ ...current, severity: event.target.value }))}
                        options={INCIDENT_SEVERITY_OPTIONS}
                      />
                    </div>
                  </div>
                  <div>
                    <CFormLabel htmlFor="admin-trust-incident-summary">Complaint / Incident Summary</CFormLabel>
                    <CFormTextarea
                      id="admin-trust-incident-summary"
                      rows={4}
                      value={actionDraft.summary}
                      onChange={(event) => setActionDraft((current) => ({ ...current, summary: event.target.value }))}
                      placeholder="Was wurde gemeldet, warum ist es relevant, welche Trust-Auswirkung wird erwartet?"
                    />
                  </div>
                </>
              ) : null}

              {currentActionIsHighImpact ? (
                <div className="rounded border border-danger-subtle bg-danger-subtle p-3">
                  <div className="text-uppercase small text-danger-emphasis">Vorher / Nachher</div>
                  <div className="small mt-2">{currentActionSafetySummary?.before || "Vorher-Zusammenfassung nicht verfuegbar."}</div>
                  <div className="small">{currentActionSafetySummary?.after || "Nachher-Zusammenfassung nicht verfuegbar."}</div>
                </div>
              ) : null}

              <div>
                <CFormLabel htmlFor="admin-trust-reason">Begruendung</CFormLabel>
                <CFormTextarea
                  id="admin-trust-reason"
                  rows={4}
                  value={actionDraft.reason}
                  onChange={(event) => setActionDraft((current) => ({ ...current, reason: event.target.value }))}
                  placeholder="Pflichtfeld: Grund, Kontext, Admin-Entscheidung"
                />
              </div>

              {currentActionIsHighImpact ? (
                <CFormCheck
                  id="admin-trust-impact-confirm"
                  checked={Boolean(actionDraft.impact_confirmed)}
                  onChange={(event) => setActionDraft((current) => ({ ...current, impact_confirmed: event.target.checked }))}
                  label="Ich habe Live-Auswirkung, Bewerberreichweite und Vorher/Nachher-Folgen geprüft."
                />
              ) : null}

              {actionDraft.action === "release_affected_ads" && !currentEmployer?.canPublishLive ? (
                <CCallout color="danger">
                  <strong>Release aktuell blockiert.</strong>
                  <div>Betroffene Anzeigen duerfen erst wieder freigegeben werden, wenn der Employer Trust wieder sauber live-faehig ist.</div>
                </CCallout>
              ) : null}
            </div>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setActionDraft(null)}>
            Abbrechen
          </CButton>
          <CLoadingButton
            color={currentActionIsHighImpact ? "danger" : "primary"}
            loading={actionBusy}
            disabled={
              !actionDraft?.reason?.trim()
              || (currentActionMeta?.requiresIncident && !actionDraft?.incident_id)
              || (currentActionMeta?.supportsComplaintConversion && !actionDraft?.summary?.trim())
              || (currentActionIsHighImpact && !actionDraft?.impact_confirmed)
            }
            onClick={runAction}
          >
            {currentActionIsHighImpact ? "High-Impact-Aktion bestaetigen" : "Aktion ausfuehren"}
          </CLoadingButton>
        </CModalFooter>
      </CModal>
    </AdminPageFrame>
  );
}
